import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ESTIMATING EDIT PAGE
 *
 * Page for editing estimation/quote details with work breakdown
 * Uses the same work details structure as the Site page
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button, Checkbox, CommencementDateInput, DateInput, PageHeader, PageHeaderWithBack, useToast, } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useDeposit } from "@/context/DepositContext";
import { quotesService, } from "@/api/services/quotes.service";
import { useCreateJobFromQuote } from "./hooks/useCreateJobFromQuote";
import { sitesService } from "@/api/services/sites.service";
import { applyMainOtherFromCostParams, } from "./mainJobOtherUtils";
import { contactsService } from "@/api/services/contacts.service";
import { formatFullName, getContactDisplayName } from "@/utils/string";
import { calculateTotalCost, getSectionAfterHoursStatus, getShopfrontSettingsFromItems, } from "@/utils/costCalculations";
import { matchesQuoteStatus, matchesDefitType, getQuoteAssignedContactId, getQuoteStatusForDisplay, } from "@/utils/quoteHelpers";
import { isWeekend } from "@/utils/dateTime";
import { isUuid } from "@/utils/validation";
import { findShoppingCentreIdByName } from "@/utils/shoppingCentreMatch";
import { EstimatingQuoteDocumentsTab, EstimatingNotificationsTab, } from "./components";
import { useMessagesUnreadBadge } from "@/hooks/useMessagesUnreadBadge";
import { SearchableShoppingCentreSelect } from "@/features/admin/contacts/components/SearchableShoppingCentreSelect";
import styles from "./EstimatingEdit.module.css";
const ESTIMATE_TABS = [
    { id: "detail", label: "Estimate Detail" },
    { id: "documents", label: "Documents and Photos" },
    { id: "notifications", label: "Messages" },
];
// Work category tabs - same as Site page
const WORK_DETAILS_CATEGORY_TABS = [
    { id: "compliance", label: "Compliance/Startup" },
    { id: "service_trades", label: "Service Trades" },
    { id: "flooring", label: "Flooring" },
    { id: "internal_demolition", label: "Internal Demolition" },
    { id: "preliminary_cost", label: "Preliminary Cost" },
    { id: "summary", label: "Summary" },
];
// Default area information template
const DEFAULT_AREA_INFO = [
    { label: "Tenancy Defit (m2)", value: 0 },
    { label: "Wall Finish (Plaster) (m2)", value: 0 },
    { label: "Wall Finish (Brickwork) (m2)", value: 0 },
    { label: "Shopfront (l/m)", value: 0 },
    { label: "Shopfront (under 3.6m) OR of Kiosk Lm (m2)", value: 0 },
];
// Helper function to merge areaInfo with default template
const mergeAreaInfo = (sourceAreaInfo, defaultTemplate) => {
    return defaultTemplate.map((defaultItem) => {
        const sourceItem = sourceAreaInfo?.find((item) => item.label === defaultItem.label) ?? null;
        return {
            ...defaultItem,
            value: sourceItem?.value ?? defaultItem.value,
        };
    });
};
// Default accordion structure for each category - same as Site page
const getDefaultAccordionData = () => ({
    compliance: [
        {
            id: "demolition",
            title: "Demolition",
            sections: [
                {
                    sectionId: "hoardings",
                    sectionTitle: "Hoardings (note rates are on m2 basis)",
                    items: [
                        {
                            id: "hoarding-internal-solid",
                            title: "Internal Solid Hoarding",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hoarding-kiosk",
                            title: "Kiosk Hoarding",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "compliance-main",
            title: "Compliance",
            sections: [
                {
                    sectionId: "permits",
                    items: [
                        {
                            id: "comp-demolition-permit",
                            title: "Demolition Permit",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "comp-building-permit",
                            title: "Building Permit",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    service_trades: [
        {
            id: "services-electrical",
            title: "Services - Electrical",
            sections: [
                {
                    sectionId: "electrical",
                    items: [
                        {
                            id: "elec-1",
                            title: "Isolate and Make Safe Tenancy Single EDB and IDF",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "elec-2",
                            title: "Isolate and Make Safe Tenancy Dual EDB and IDF",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "fire",
            title: "Fire",
            sections: [
                {
                    sectionId: "fire",
                    showQty: true,
                    items: [
                        {
                            id: "fire-1",
                            title: "Sprinkler – plugging up to 3m",
                            inScope: false,
                            qty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fire-2",
                            title: "Wet System Drain Down and Recommission - note each means 1 drain down and recommission",
                            inScope: false,
                            qty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fire-3",
                            title: "Smoke Detector – relocation up to 3m",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                        {
                            id: "fire-4",
                            title: "Electronic smoke alarm isolation/de-isolation",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                        {
                            id: "fire-5",
                            title: "EWIS – relocation up to 10m with Fig. 8 cable",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "hydraulics",
            title: "Hydraulics",
            sections: [
                {
                    sectionId: "hydraulics",
                    showQty: true,
                    items: [
                        {
                            id: "hyd-1",
                            title: "Attendance including Cap and Seal Hydraulics Points - 1 water and 1 waste water",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                        {
                            id: "hyd-2",
                            title: "Cap and Seal additional hydraulics points - 1 water and 1 waste water",
                            inScope: false,
                            qty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hyd-3",
                            title: "Penetration of plaster ceiling and backfill",
                            inScope: false,
                            qty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "hvac",
            title: "HVAC - Tenant Supplementary Units",
            sections: [
                {
                    sectionId: "hvac",
                    items: [
                        {
                            id: "hvac-1",
                            title: "Split ducted - In ceiling space air cooled FCU with external condenser",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hvac-2",
                            title: "Split cassette - Ceiling mounted integrated air cooled unit with external condenser",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hvac-3",
                            title: "Wall hung split - Wall mounted air cooled unit with external condenser",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hvac-4",
                            title: "Split ducted - water cooled no or internally located condenser",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "it-wall",
            title: "IT Wall",
            sections: [
                {
                    sectionId: "it-wall",
                    items: [
                        {
                            id: "it-wall-1",
                            title: "Supply and install plasterboard to perimeter IT walls",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-2",
                            title: "Supply and install new IT wall (13mm Plasterboard either side with 92mm Stud)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-3",
                            title: "Relocate EDB (Switchboard)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-4",
                            title: "Supply Paint",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-5",
                            title: "Relocate Hydraulic Services (underground trenching)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-6",
                            title: "Relocate Hydraulic Services (above ground)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    flooring: [
        {
            id: "floor-finishes",
            title: "Floor Finishes",
            sections: [
                {
                    sectionId: "floor-finishes",
                    items: [
                        {
                            id: "ff-1",
                            title: "Remove Ceramic Tiles (Direct Stick)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ff-2",
                            title: "Remove Vinyl (Walk behind)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ff-3",
                            title: "Remove Carpet Tiles",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ff-4",
                            title: "Remove Timber / Parquetry (Direct Stick)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ff-5",
                            title: "After Hours Works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "screeds",
            title: "Screeds",
            sections: [
                {
                    sectionId: "screeds",
                    items: [
                        {
                            id: "screeds-1",
                            title: "0mm - 50mm",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "screeds-2",
                            title: "50mm - 80mm",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "screeds-3",
                            title: "After Hours works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "grinding",
            title: "Grinding",
            sections: [
                {
                    sectionId: "grinding",
                    items: [
                        {
                            id: "grinding-1",
                            title: "Tenancy Floor Grinding",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "grinding-2",
                            title: "After Hours works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    internal_demolition: [
        {
            id: "kiosks",
            title: "Kiosks",
            sections: [
                {
                    sectionId: "kiosks",
                    items: [
                        {
                            id: "kiosks-1",
                            title: "Specialty",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "kiosks-2",
                            title: "Food & Beverage",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "kiosks-3",
                            title: "Make Good of Mall Tiles",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "joinery-fixtures",
            title: "Joinery & Fixtures",
            sections: [
                {
                    sectionId: "joinery",
                    sectionTitle: "Tenancy Area (0-500m2)",
                    items: [
                        {
                            id: "joinery-0-500-low",
                            title: "Low volume of FF&E & Joinery Disposal (General Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-0-500-moderate",
                            title: "Moderate volume of FF&E & Joinery Disposal (Premium Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-0-500-high-food",
                            title: "High volume of FF&E & Joinery Disposal (Food Service incl. Cool Room)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-0-500-high-jewellers",
                            title: "High volume of FF&E & Joinery Disposal (Jewellers)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
                {
                    sectionId: "joinery",
                    sectionTitle: "Tenancy Area (500-1000m2)",
                    items: [
                        {
                            id: "joinery-500-1000-low",
                            title: "Low volume of FF&E & Joinery Disposal (General Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-500-1000-moderate",
                            title: "Moderate volume of FF&E & Joinery Disposal (Premium Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-500-1000-high-food",
                            title: "High volume of FF&E & Joinery Disposal (Food Service incl. Cool Room)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-500-1000-high-jewellers",
                            title: "High volume of FF&E & Joinery Disposal (Jewellers)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
                {
                    sectionId: "joinery",
                    sectionTitle: "Tenancy Area (1000m2+)",
                    items: [
                        {
                            id: "joinery-1000-plus-low",
                            title: "Low volume of FF&E & Joinery Disposal (General Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-1000-plus-moderate",
                            title: "Moderate volume of FF&E & Joinery Disposal (Premium Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-1000-plus-high-food",
                            title: "High volume of FF&E & Joinery Disposal (Food Service incl. Cool Room)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-1000-plus-high-jewellers",
                            title: "High volume of FF&E & Joinery Disposal (Jewellers)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "demolish-walls",
            title: "Demolish Walls",
            sections: [
                {
                    sectionId: "walls",
                    items: [
                        {
                            id: "walls-1",
                            title: "Remove Blockwork Walls (Single Skin)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "walls-2",
                            title: "Remove Stud IT Walls",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "walls-3",
                            title: "After Hours works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "demolish-shopfronts",
            title: "Demolish Shopfronts",
            sections: [
                {
                    sectionId: "shopfronts",
                    items: [
                        {
                            id: "shopfront-1",
                            title: "Standard glazed & MDF",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-2",
                            title: "Heavy Duty Steel & Stone",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-3",
                            title: "Shopfront Height Factor Std",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-4",
                            title: "Shopfront Height Factor Hvy",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-3a",
                            title: "Shopfront is OVER 3.6m in height",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-5",
                            title: "After Hours Works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "ceiling-type",
            title: "Ceiling Type",
            sections: [
                {
                    sectionId: "ceiling",
                    sectionTitle: "Ceiling Type - to include demolition of mechanical diffusers",
                    items: [
                        {
                            id: "ceiling-1",
                            title: "Open Plan Ceiling",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ceiling-2",
                            title: "Remove Ceiling Tiles & Grid",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ceiling-3",
                            title: "Remove Plaster Lining & Gridwork",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ceiling-4",
                            title: "After Hours Works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    preliminary_cost: [
        {
            id: "prelims-supervision",
            title: "Prelims & Supervision",
            sections: [
                {
                    sectionId: "prelims",
                    items: [
                        {
                            id: "prelims-1",
                            title: "Prelims & Supervision",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "scissor-lift",
            title: "Scissor Lift Hire",
            sections: [
                {
                    sectionId: "scissor-lift",
                    showWeeksQty: true,
                    items: [
                        {
                            id: "scissor-1",
                            title: '"19" Electric',
                            inScope: false,
                            weeksQty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "skip-hire",
            title: "Skip Hire",
            sections: [
                {
                    sectionId: "skip-hire",
                    showNumberQty: true,
                    items: [
                        {
                            id: "skip-1",
                            title: "12m3",
                            inScope: false,
                            numberQty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "skip-2",
                            title: "15m3",
                            inScope: false,
                            numberQty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "skip-3",
                            title: "23m3",
                            inScope: false,
                            numberQty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "other-fees",
            title: "Other Fees Price on Application",
            sections: [
                {
                    sectionId: "other-fees",
                    items: [
                        {
                            id: "fees-1",
                            title: "Specialist consultant - Electrical",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fees-2",
                            title: "Specialist consultant - Hydraulic",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fees-3",
                            title: "Specialist consultant - Structural",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fees-4",
                            title: "Protection works",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fees-5",
                            title: "Specialist Cleaning",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    summary: [],
});
// Helper to parse currency string to number
const parseCurrency = (value) => {
    if (!value)
        return 0;
    const cleaned = value.replace(/[^0-9.-]/g, "");
    return parseFloat(cleaned) || 0;
};
// Helper to format number as currency
const formatCurrency = (value) => {
    return `$${value.toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};
// Helper to calculate sum of Standard glazed & MDF and Heavy Duty Steel & Stone totals
const getShopfrontItemsTotal = (items, shopfrontSettings) => {
    let total = 0;
    items.forEach((item) => {
        const isStandardGlazed = item.id === "shopfront-1" ||
            item.title.toLowerCase().includes("standard glazed") ||
            item.title.toLowerCase().includes("standard");
        const isHeavyDuty = item.id === "shopfront-2" ||
            item.title.toLowerCase().includes("heavy duty") ||
            item.title.toLowerCase().includes("heavy");
        if ((isStandardGlazed || isHeavyDuty) && item.inScope && item.totalCost) {
            let itemTotal = parseCurrency(item.totalCost);
            // Apply height factor if toggle is ON
            if (shopfrontSettings?.heightEnabled) {
                if (isStandardGlazed) {
                    itemTotal *= shopfrontSettings.stdHeightFactor;
                }
                else if (isHeavyDuty) {
                    itemTotal *= shopfrontSettings.hvyHeightFactor;
                }
            }
            total += itemTotal;
        }
    });
    return total;
};
// Helper to format unit cost for display (decimal without $)
export const EstimatingEdit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { estimateId } = useParams();
    const { user, isAdmin } = useAuth();
    const { success, error: showErrorToast } = useToast();
    const canEditPrices = isAdmin; // Only admins can edit prices
    const { createJob, isCreating } = useCreateJobFromQuote();
    // Determine base path (admin or staff)
    const basePath = location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    // Quote data
    const [quote, setQuote] = useState(null);
    // Form state
    const [siteId, setSiteId] = useState("");
    const [selectedContactId, setSelectedContactId] = useState("");
    const [attentionContactId, setAttentionContactId] = useState("");
    const [clientName, setClientName] = useState("");
    const [defitType, setDefitType] = useState("full");
    const [quoteStatus, setQuoteStatus] = useState("draft");
    const [quoteName, setQuoteName] = useState("");
    const [quoteDescription, setQuoteDescription] = useState("");
    const [commencementDate, setCommencementDate] = useState("");
    const [commencementTime, setCommencementTime] = useState("");
    const [completionDate, setCompletionDate] = useState("");
    const [completionTime, setCompletionTime] = useState("");
    const [projectDuration, setProjectDuration] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // Sites for dropdown
    const [sites, setSites] = useState([]);
    // Site details for display (when linked to a site from quote request)
    const [linkedSiteName, setLinkedSiteName] = useState("");
    const [linkedSiteSuburb, setLinkedSiteSuburb] = useState("");
    const [linkedSiteAddress, setLinkedSiteAddress] = useState("");
    const [linkedSiteState, setLinkedSiteState] = useState("");
    const [linkedSitePostcode, setLinkedSitePostcode] = useState("");
    const [shoppingCentreId, setShoppingCentreId] = useState("");
    // Searchable company dropdown state (like Sites page)
    const companyDropdownRef = useRef(null);
    const [companyNames, setCompanyNames] = useState([]);
    const [companyContactIdMap, setCompanyContactIdMap] = useState(new Map());
    const [loadingCompanyNames, setLoadingCompanyNames] = useState(false);
    const [companySearchTerm, setCompanySearchTerm] = useState("");
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [selectedCompanyName, setSelectedCompanyName] = useState("");
    // Company contacts for attention field
    const [companyContacts, setCompanyContacts] = useState([]);
    // Searchable attention dropdown state
    const attentionDropdownRef = useRef(null);
    const [attentionSearchTerm, setAttentionSearchTerm] = useState("");
    const [showAttentionDropdown, setShowAttentionDropdown] = useState(false);
    const [selectedAttentionContactName, setSelectedAttentionContactName] = useState("");
    // Area information
    const [areaInfo, setAreaInfo] = useState(DEFAULT_AREA_INFO);
    // Cost parameters state
    const [costParameters, setCostParameters] = useState([]);
    const [costParametersLoaded, setCostParametersLoaded] = useState(false);
    // Mirror of costParameters readable inside callbacks (e.g. mergeWorkItems)
    // without re-creating them on every cost-params change. Used to union the
    // Main Job "Other" rows when the saved-quote load runs after cost params.
    const costParametersRef = useRef([]);
    useEffect(() => {
        costParametersRef.current = costParameters;
    }, [costParameters]);
    // Work details state
    const [activeWorkCategory, setActiveWorkCategory] = useState("compliance");
    const [expandedAccordions, setExpandedAccordions] = useState(new Set());
    const [accordionData, setAccordionData] = useState(getDefaultAccordionData);
    const [refreshingPrices, setRefreshingPrices] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    // Top-level tab (Estimate Detail | Documents and Photos | Notifications)
    const [activeEstimateTab, setActiveEstimateTab] = useState("detail");
    const { unreadCount, clearBadge } = useMessagesUnreadBadge({
        type: "quote",
        entityId: quote?.id ?? estimateId ?? "",
        contactId: selectedContactId,
    });
    const { defaultDepositPercentage } = useDeposit();
    useEffect(() => {
        const tabFromState = location.state?.tab;
        if (tabFromState &&
            ["detail", "documents", "notifications"].includes(tabFromState)) {
            setActiveEstimateTab(tabFromState);
        }
    }, [location.state?.tab]);
    // Financial state
    const [buildersMarginEnabled, setBuildersMarginEnabled] = useState(true);
    const [buildersMarginPercentage, setBuildersMarginPercentage] = useState(10);
    const [includeNewClientTerms, setIncludeNewClientTerms] = useState(false);
    const [depositEnabled, setDepositEnabled] = useState(false);
    const [depositPercentage, setDepositPercentage] = useState(defaultDepositPercentage);
    useEffect(() => {
        if (includeNewClientTerms && !depositEnabled) {
            setDepositEnabled(true);
        }
    }, [includeNewClientTerms, depositEnabled]);
    // Fetch sites for dropdown
    const fetchSites = useCallback(async () => {
        try {
            const response = await sitesService.getSites({ pageSize: 100 });
            const siteOptions = response.data.data.map((site) => ({
                id: site.id,
                name: site.siteName,
            }));
            setSites(siteOptions);
        }
        catch (err) {
            console.error("Error fetching sites:", err);
        }
    }, []);
    // Fetch company names and contactId mapping for searchable dropdown
    const fetchCompanyNames = useCallback(async () => {
        try {
            setLoadingCompanyNames(true);
            // Fetch companies with their contact IDs so we can look up contactId when selecting
            const response = await contactsService.getCompaniesWithContactIds();
            const companiesData = response.data.data || [];
            // Build the company name -> contactId map
            const newMap = new Map();
            companiesData.forEach((company) => {
                if (company.companyName && company.contactId) {
                    newMap.set(company.companyName, company.contactId);
                }
            });
            setCompanyContactIdMap(newMap);
            // Extract unique company names and filter based on search term
            const allCompanyNames = Array.from(newMap.keys());
            let filteredNames = allCompanyNames;
            if (companySearchTerm && companySearchTerm.trim()) {
                const searchLower = companySearchTerm.toLowerCase().trim();
                filteredNames = allCompanyNames.filter((name) => name.toLowerCase().includes(searchLower));
            }
            setCompanyNames(filteredNames);
        }
        catch (err) {
            console.error("Error fetching company names:", err);
            setCompanyNames([]);
        }
        finally {
            setLoadingCompanyNames(false);
        }
    }, [companySearchTerm]);
    // Fetch cost parameters
    const fetchCostParameters = useCallback(async () => {
        try {
            const response = await sitesService.getDefaultWorkItems();
            setCostParameters(response.data);
            setCostParametersLoaded(true);
        }
        catch (err) {
            console.error("Error fetching cost parameters:", err);
            setCostParametersLoaded(true); // Still mark as loaded even on error
        }
    }, []);
    // Convert ISO date to YYYY-MM-DD for input[type="date"]
    const formatDateForInput = (isoDate) => {
        if (!isoDate)
            return "";
        const date = new Date(isoDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };
    // Merge work items from quote lineItems into accordion structure
    const mergeWorkItems = useCallback((lineItems) => {
        if (!lineItems || lineItems.length === 0)
            return;
        const newData = getDefaultAccordionData();
        // Seed the Main Job "Other" rows (group2–group5) from the saved line
        // items so the generic loop below can apply their scoping by id.
        applyMainOtherFromCostParams(newData, lineItems);
        lineItems.forEach((item) => {
            const category = item.category?.toLowerCase();
            if (!category || !newData[category])
                return;
            const accordionItem = newData[category].find((acc) => acc.sections?.some((s) => s.sectionId === item.sectionId));
            if (accordionItem?.sections) {
                const section = accordionItem.sections.find((s) => {
                    if (s.sectionId === item.sectionId) {
                        // For joinery sections, also match by sectionTitle
                        if (item.sectionId === "joinery" && item.sectionTitle) {
                            return s.sectionTitle === item.sectionTitle;
                        }
                        return true;
                    }
                    return false;
                });
                if (section) {
                    const subItem = section.items.find((i) => i.id === item.itemId);
                    if (subItem) {
                        subItem.inScope = item.inScope;
                        // Store defaultUnitCost for later use (e.g., when toggling After Hours items)
                        subItem.defaultUnitCost = item.defaultUnitCost ?? null;
                        // Use saved unitCost if set, otherwise use defaultUnitCost from cost parameters
                        const unitCostValue = item.unitCost != null
                            ? item.unitCost
                            : (item.defaultUnitCost ?? null);
                        subItem.unitCost = unitCostValue != null ? `${unitCostValue}` : "";
                        subItem.totalCost =
                            item.totalCost != null ? `$${item.totalCost}` : "";
                        subItem.notes = item.notes || "";
                        // Map quantity to the appropriate field based on sectionId
                        // Prioritize weeksQty/numberQty if they exist, otherwise use quantity
                        if (item.weeksQty) {
                            subItem.weeksQty = item.weeksQty.toString();
                        }
                        else if (item.numberQty) {
                            subItem.numberQty = item.numberQty.toString();
                        }
                        else if (item.quantity) {
                            // Map quantity to the appropriate field based on sectionId
                            if (section.sectionId === "scissor-lift") {
                                subItem.weeksQty = item.quantity.toString();
                            }
                            else if (section.sectionId === "skip-hire") {
                                subItem.numberQty = item.quantity.toString();
                            }
                            else {
                                subItem.qty = item.quantity.toString();
                            }
                        }
                        if (item.selectedOption) {
                            subItem.selectedOption = item.selectedOption;
                        }
                    }
                }
            }
        });
        // Union Main Job "Other" rows from current Cost Settings on top of the
        // lineItems-seeded accordion, so newly-added Other rows surface here even
        // when this runs after the cost-params load (via a ref to read the latest
        // value without forcing a useCallback recompute / re-fetch loop).
        applyMainOtherFromCostParams(newData, costParametersRef.current);
        setAccordionData(newData);
    }, []);
    // Map cost parameters to accordion data structure
    const mapCostParametersToAccordion = useCallback((defaultData, costParams) => {
        const mappedData = { ...defaultData };
        costParams.forEach((cp) => {
            const category = cp.category;
            if (!mappedData[category])
                return;
            // Find the matching accordion item
            const accordionItem = mappedData[category].find((item) => item.sections?.some((section) => section.sectionId === cp.sectionId));
            if (accordionItem) {
                // Search through all sections to find the item by itemId
                // (multiple sections can have the same sectionId, e.g., joinery)
                for (const section of accordionItem.sections || []) {
                    const subItem = section.items.find((item) => item.id === cp.itemId);
                    if (subItem) {
                        // Store defaultUnitCost from cost settings for later use (e.g., when toggling After Hours items)
                        subItem.defaultUnitCost =
                            cp.defaultUnitCost ?? cp.unitCost ?? null;
                        if (cp.unitCost) {
                            // Update unit cost as plain number string (preserves decimals, matching SiteDetailsTab behavior)
                            subItem.unitCost = `${cp.unitCost}`;
                        }
                        break; // Found the item, no need to continue searching
                    }
                }
            }
        });
        // Surface the client-editable Main Job "Other" rows (group2–group5),
        // which arrive with prefixed sectionIds the per-item loop can't match.
        applyMainOtherFromCostParams(mappedData, costParams);
        return mappedData;
    }, []);
    // Load quote data
    const loadQuoteData = useCallback(async () => {
        if (!estimateId)
            return;
        try {
            setLoading(true);
            setError(null);
            const response = await quotesService.getQuote(estimateId);
            const quoteData = response.data;
            let derivedShoppingCentreId = quoteData.shoppingCentreId ?? quoteData.shoppingCentre?.id ?? "";
            setQuote(quoteData);
            setSiteId(quoteData.siteId || "");
            setSelectedContactId(getQuoteAssignedContactId(quoteData));
            setAttentionContactId(quoteData.attentionContactId || "");
            // Fetch linked site details if siteId exists
            if (quoteData.siteId) {
                try {
                    const siteResponse = await sitesService.getSite(quoteData.siteId);
                    const siteData = siteResponse.data;
                    setLinkedSiteName(siteData.siteName || "");
                    setLinkedSiteSuburb(siteData.suburb || "");
                    setLinkedSiteAddress(siteData.address || "");
                    setLinkedSiteState(siteData.state || "");
                    setLinkedSitePostcode(siteData.postcode || "");
                    if (!(quoteData.shoppingCentreId ?? quoteData.shoppingCentre?.id)) {
                        derivedShoppingCentreId =
                            siteData.shoppingCentreId ??
                                siteData.shoppingCentre?.id ??
                                (isUuid(siteData.shoppingCenter ?? "")
                                    ? siteData.shoppingCenter
                                    : "") ??
                                "";
                    }
                }
                catch (siteErr) {
                    console.error("Error fetching linked site details:", siteErr);
                    setLinkedSiteName("");
                    setLinkedSiteSuburb("");
                    setLinkedSiteAddress("");
                    setLinkedSiteState("");
                    setLinkedSitePostcode("");
                }
            }
            else {
                setLinkedSiteName("");
                setLinkedSiteSuburb("");
                setLinkedSiteAddress("");
                setLinkedSiteState("");
                setLinkedSitePostcode("");
            }
            setShoppingCentreId(derivedShoppingCentreId);
            setDefitType(matchesDefitType(quoteData.defitType, "full") ? "full" : "partial");
            setQuoteStatus(getQuoteStatusForDisplay(quoteData.status));
            setQuoteName(quoteData.title);
            setQuoteDescription(quoteData.description || "");
            setCommencementDate(formatDateForInput(quoteData.commencementDate));
            setCommencementTime(quoteData.commencementTime || "");
            setCompletionDate(formatDateForInput(quoteData.completionDate));
            setCompletionTime(quoteData.completionTime || "");
            setProjectDuration(quoteData.projectDuration?.toString() || "");
            // Merge quote areaInfo with default template so all items are always visible
            setAreaInfo(mergeAreaInfo(quoteData.areaInfo, DEFAULT_AREA_INFO));
            // Financial state
            setBuildersMarginEnabled(quoteData.buildersMarginEnabled);
            setBuildersMarginPercentage(quoteData.buildersMarginPercentage || 10);
            setIncludeNewClientTerms(quoteData.includeNewClientPaymentTerms);
            setDepositEnabled(quoteData.depositEnabled);
            setDepositPercentage(quoteData.depositPercentage ?? defaultDepositPercentage);
            // Load work items into accordion
            if (quoteData.lineItems && quoteData.lineItems.length > 0) {
                mergeWorkItems(quoteData.lineItems);
            }
            else {
                // Quote Request with no work items - will be initialized with cost parameters
                // via useEffect when cost parameters are loaded
            }
            // Fetch company contacts for attention field and set company name
            if (quoteData.contactId) {
                try {
                    // First, get the contact detail to get company name
                    const contactResponse = await contactsService.getContact(quoteData.contactId, 1, // page
                    1000);
                    const contactData = contactResponse.data;
                    if (!derivedShoppingCentreId && contactData.shoppingCenter?.trim()) {
                        const matched = await findShoppingCentreIdByName(contactData.shoppingCenter.trim());
                        if (matched)
                            derivedShoppingCentreId = matched;
                    }
                    // Set the company name for display in the searchable dropdown
                    if (contactData.companyName) {
                        setSelectedCompanyName(contactData.companyName);
                        setCompanySearchTerm(contactData.companyName);
                        // Fetch all company contacts using the same method as manual selection
                        // This ensures consistency and that all contacts are available
                        try {
                            const companyContactsResponse = await contactsService.getContacts({
                                companyName: contactData.companyName,
                                page: 1,
                            });
                            // Map Contact[] to CompanyContact[] format (same as manual selection)
                            const contacts = (companyContactsResponse.data.data || []).map((contact) => ({
                                contactId: contact.contactId,
                                firstName: contact.firstName,
                                lastName: contact.lastName,
                                email: contact.email,
                                phone: contact.phone,
                                role: contact.role,
                            }));
                            setCompanyContacts(contacts);
                            // Set the attention contact display name and clientName
                            if (quoteData.attentionContactId && contacts.length > 0) {
                                const attentionContact = contacts.find((c) => c.contactId === quoteData.attentionContactId);
                                if (attentionContact) {
                                    const displayName = getContactDisplayName(attentionContact.firstName, attentionContact.lastName, attentionContact.email);
                                    setSelectedAttentionContactName(displayName);
                                    setAttentionSearchTerm(displayName);
                                    // Keep clientName as company name (Client = company, Attention = contact)
                                    setClientName(contactData.companyName || quoteData.clientName || "");
                                }
                                else {
                                    // Attention contact not found in company contacts
                                    // Fall back to clientName or company name
                                    if (quoteData.clientName) {
                                        setClientName(quoteData.clientName);
                                    }
                                    else if (contactData.companyName) {
                                        setClientName(contactData.companyName);
                                    }
                                }
                            }
                            else if (quoteData.clientName) {
                                // If no attention contact but clientName exists, use it
                                setClientName(quoteData.clientName);
                            }
                            else if (contactData.companyName) {
                                // Fall back to company name
                                setClientName(contactData.companyName);
                            }
                        }
                        catch (contactsErr) {
                            console.error("Error fetching company contacts list:", contactsErr);
                            // Fallback to companyContacts from getContact if available
                            setCompanyContacts(contactData.companyContacts || []);
                            // Still try to set attention contact from companyContacts if available
                            if (quoteData.attentionContactId && contactData.companyContacts) {
                                const attentionContact = contactData.companyContacts.find((c) => c.contactId === quoteData.attentionContactId);
                                if (attentionContact) {
                                    const displayName = getContactDisplayName(attentionContact.firstName, attentionContact.lastName, attentionContact.email);
                                    setSelectedAttentionContactName(displayName);
                                    setAttentionSearchTerm(displayName);
                                    // Keep clientName as company name (Client = company, Attention = contact)
                                    setClientName(contactData.companyName || quoteData.clientName || "");
                                }
                            }
                        }
                    }
                    else {
                        // No company name, can't fetch company contacts
                        setCompanyContacts([]);
                        if (quoteData.clientName) {
                            setClientName(quoteData.clientName);
                        }
                    }
                }
                catch (err) {
                    console.error("Error fetching company contacts:", err);
                    setCompanyContacts([]);
                    if (quoteData.clientName) {
                        setClientName(quoteData.clientName);
                    }
                }
            }
            setShoppingCentreId(derivedShoppingCentreId);
        }
        catch (err) {
            setError("Failed to load quote data");
            console.error("Error loading quote:", err);
        }
        finally {
            setLoading(false);
        }
    }, [estimateId, mergeWorkItems]);
    useEffect(() => {
        fetchSites();
        loadQuoteData();
        fetchCostParameters();
    }, [fetchSites, loadQuoteData, fetchCostParameters]);
    // Initialize accordion with cost parameters for Quote Requests with no lineItems
    useEffect(() => {
        if (costParametersLoaded &&
            costParameters.length > 0 &&
            quote &&
            (!quote.lineItems || quote.lineItems.length === 0)) {
            const defaultData = getDefaultAccordionData();
            const mappedData = mapCostParametersToAccordion(defaultData, costParameters);
            setAccordionData(mappedData);
        }
    }, [
        costParametersLoaded,
        costParameters,
        quote,
        mapCostParametersToAccordion,
    ]);
    // Surface Main Job "Other" rows added in Cost Settings after this quote was
    // saved. Compares the current default-work-items against the accordion built
    // from saved lineItems and unions in any missing rows (inScope:false), while
    // preserving the estimator state already applied for existing rows.
    // Depends on `quote` so it re-runs after mergeWorkItems (which rebuilds the
    // accordion from fresh defaults + saved lineItems) has settled — otherwise
    // a cost-params-first / quote-second load order would wipe the union.
    useEffect(() => {
        if (!costParametersLoaded || costParameters.length === 0)
            return;
        setAccordionData((prevData) => {
            const newData = JSON.parse(JSON.stringify(prevData));
            applyMainOtherFromCostParams(newData, costParameters);
            return newData;
        });
    }, [costParametersLoaded, costParameters, quote]);
    // Fetch company names when dropdown is shown
    useEffect(() => {
        if (showCompanyDropdown) {
            fetchCompanyNames();
        }
    }, [showCompanyDropdown, fetchCompanyNames]);
    // Handle click outside to close company dropdown
    useEffect(() => {
        if (!showCompanyDropdown)
            return;
        const container = companyDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && target && !container.contains(target)) {
                setShowCompanyDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowCompanyDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showCompanyDropdown]);
    // Handle click outside to close attention dropdown
    useEffect(() => {
        if (!showAttentionDropdown)
            return;
        const container = attentionDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && target && !container.contains(target)) {
                setShowAttentionDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowAttentionDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showAttentionDropdown]);
    // Recalculate area-based items when area info changes
    useEffect(() => {
        if (!costParametersLoaded)
            return;
        console.log("Area info changed, recalculating work items...", areaInfo);
        setAccordionData((prevData) => {
            const newData = { ...prevData };
            let totalItemsRecalculated = 0;
            // Iterate through all categories and sections to recalculate area-based items
            Object.keys(newData).forEach((categoryKey) => {
                const category = categoryKey;
                const categoryItems = [...newData[category]];
                categoryItems.forEach((item, itemIndex) => {
                    if (item.sections) {
                        const sections = [...item.sections];
                        sections.forEach((section, sectionIndex) => {
                            const items = [...section.items];
                            let hasChanges = false;
                            // Helper to check if item is After Hours
                            const isAfterHoursItem = (itemTitle) => itemTitle.toLowerCase().includes("after hours");
                            // Check After Hours status directly from items array (not from prevData which has stale state)
                            const afterHoursItem = items.find((item) => item.title.toLowerCase().includes("after hours"));
                            const afterHoursEnabled = afterHoursItem?.inScope || false;
                            const shopfrontSettings = getShopfrontSettingsFromItems(items, costParameters);
                            // Find Float value from Cost Settings (costParameters) for "19" Electric calculation
                            const isScissorLiftSection = section.sectionId === "scissor-lift";
                            const floatCostParam = isScissorLiftSection
                                ? costParameters.find((cp) => cp.category === "preliminary_cost" &&
                                    cp.sectionId === "scissor-lift" &&
                                    cp.itemTitle?.toLowerCase().includes("float"))
                                : null;
                            const floatValue = floatCostParam?.unitCost ?? undefined;
                            // First pass: Calculate all non-After-Hours items
                            items.forEach((subItem, subItemIndex) => {
                                if (subItem.inScope && !isAfterHoursItem(subItem.title)) {
                                    const isShopfrontOver36m = subItem.title
                                        .toLowerCase()
                                        .includes("over 3.6m");
                                    // For OVER 3.6m item, calculate even without unit cost
                                    if (isShopfrontOver36m &&
                                        section.sectionId === "shopfronts") {
                                        const shopfrontItemsTotal = getShopfrontItemsTotal(items, shopfrontSettings);
                                        const calculationResult = calculateTotalCost({
                                            itemId: subItem.id,
                                            sectionId: section.sectionId,
                                            category,
                                            title: subItem.title,
                                            unitCost: 0, // Not used for OVER 3.6m
                                            areaInfo,
                                            afterHoursEnabled,
                                            shopfrontItemsTotal,
                                        });
                                        const newTotalCost = formatCurrency(calculationResult.totalCost);
                                        if (newTotalCost !== subItem.totalCost) {
                                            items[subItemIndex] = {
                                                ...items[subItemIndex],
                                                totalCost: newTotalCost,
                                            };
                                        }
                                    }
                                    else if (subItem.unitCost) {
                                        // For other items, require unit cost
                                        const unitCost = parseCurrency(subItem.unitCost);
                                        if (unitCost > 0) {
                                            // Check if this is "19" Electric in scissor-lift section
                                            const is19Electric = isScissorLiftSection &&
                                                subItem.title.toLowerCase().includes("19") &&
                                                subItem.title.toLowerCase().includes("electric");
                                            // Calculate base total cost (without height factor)
                                            const calculationResult = calculateTotalCost({
                                                itemId: subItem.id,
                                                sectionId: section.sectionId,
                                                category,
                                                title: subItem.title,
                                                unitCost,
                                                quantity: subItem.qty
                                                    ? parseFloat(subItem.qty)
                                                    : undefined,
                                                weeksQty: subItem.weeksQty
                                                    ? parseFloat(subItem.weeksQty)
                                                    : undefined,
                                                numberQty: subItem.numberQty
                                                    ? parseFloat(subItem.numberQty)
                                                    : undefined,
                                                areaInfo,
                                                afterHoursEnabled,
                                                floatValue: is19Electric ? floatValue : undefined,
                                            });
                                            // Apply height factor for shopfront items if toggle is ON
                                            let finalTotal = calculationResult.totalCost;
                                            if (section.sectionId === "shopfronts" &&
                                                shopfrontSettings.heightEnabled) {
                                                const isStandardGlazed = subItem.id === "shopfront-1" ||
                                                    subItem.title
                                                        .toLowerCase()
                                                        .includes("standard glazed") ||
                                                    subItem.title.toLowerCase().includes("standard");
                                                const isHeavyDuty = subItem.id === "shopfront-2" ||
                                                    subItem.title.toLowerCase().includes("heavy duty") ||
                                                    subItem.title.toLowerCase().includes("heavy");
                                                if (isStandardGlazed) {
                                                    finalTotal *= shopfrontSettings.stdHeightFactor;
                                                }
                                                else if (isHeavyDuty) {
                                                    finalTotal *= shopfrontSettings.hvyHeightFactor;
                                                }
                                            }
                                            const newTotalCost = formatCurrency(finalTotal);
                                            if (newTotalCost !== subItem.totalCost) {
                                                items[subItemIndex] = {
                                                    ...items[subItemIndex],
                                                    totalCost: newTotalCost,
                                                };
                                                hasChanges = true;
                                                totalItemsRecalculated++;
                                            }
                                        }
                                    }
                                }
                            });
                            // Second pass: Handle toggle-only items and shopfront-3/4 - they should NOT have a totalCost
                            items.forEach((subItem, subItemIndex) => {
                                const isAfterHoursToggle = isAfterHoursItem(subItem.title);
                                const isShopfrontHeightToggle = section.sectionId === "shopfronts" &&
                                    (subItem.id === "shopfront-3a" ||
                                        subItem.title.toLowerCase().includes("over 3.6m"));
                                const isShopfront3Or4 = subItem.id === "shopfront-3" || subItem.id === "shopfront-4";
                                // Toggle-only items (After Hours, Shopfront OVER 3.6m) and shopfront-3/4 should NOT have a totalCost
                                if (isAfterHoursToggle ||
                                    isShopfrontHeightToggle ||
                                    isShopfront3Or4) {
                                    if (subItem.totalCost !== "") {
                                        items[subItemIndex] = {
                                            ...items[subItemIndex],
                                            totalCost: "", // Empty string instead of calculating
                                        };
                                        hasChanges = true;
                                        totalItemsRecalculated++;
                                    }
                                }
                            });
                            if (hasChanges) {
                                sections[sectionIndex] = { ...section, items };
                            }
                        });
                        if (sections.some((s) => s.items.some((i) => i.totalCost))) {
                            categoryItems[itemIndex] = { ...item, sections };
                        }
                    }
                });
                newData[category] = categoryItems;
            });
            console.log(`Total items recalculated: ${totalItemsRecalculated}`);
            return newData;
        });
    }, [areaInfo, costParametersLoaded, refreshTrigger]);
    // Handle company selection from searchable dropdown
    const handleCompanySelect = useCallback(async (companyName) => {
        setSelectedCompanyName(companyName);
        setCompanySearchTerm(companyName);
        setShowCompanyDropdown(false);
        // Set clientName to company name initially (will be replaced when attention contact is selected)
        setClientName(companyName);
        // Clear attention contact when company changes
        // This ensures Attention dropdown only shows contacts for the newly selected company
        setAttentionContactId("");
        setSelectedContactId("");
        setSelectedAttentionContactName("");
        setAttentionSearchTerm("");
        // Look up the contactId from our stored mapping (still needed for setting initial contactId)
        const contactId = companyContactIdMap.get(companyName);
        if (contactId) {
            setSelectedContactId(contactId);
        }
        // Fetch company contacts for the attention dropdown using companyName filter
        // This API call filters contacts by company name - only contacts belonging to this company are returned
        // The contacts are then stored in companyContacts state and filtered by search term in filteredAttentionContacts
        try {
            const contactResponse = await contactsService.getContacts({
                companyName: companyName,
                page: 1,
            });
            // Map Contact[] to CompanyContact[] format
            const contacts = (contactResponse.data.data || []).map((contact) => ({
                contactId: contact.contactId,
                firstName: contact.firstName,
                lastName: contact.lastName,
                email: contact.email,
                phone: contact.phone,
                role: contact.role,
            }));
            setCompanyContacts(contacts);
        }
        catch (err) {
            console.error("Error fetching company contacts:", err);
            setCompanyContacts([]);
        }
    }, [companyContactIdMap]);
    // Handle clearing company selection
    // When company is cleared, also clear all related contacts to ensure Attention dropdown
    // only shows contacts for the selected company
    const handleClearCompany = useCallback(() => {
        setSelectedCompanyName("");
        setCompanySearchTerm("");
        setSelectedContactId("");
        setAttentionContactId("");
        setSelectedAttentionContactName("");
        setAttentionSearchTerm("");
        setClientName("");
        setCompanyContacts([]); // Clear contacts when company is cleared
    }, []);
    // Handle attention contact selection
    const handleAttentionContactSelect = useCallback((contact) => {
        const displayName = getContactDisplayName(contact.firstName, contact.lastName, contact.email);
        // Set attentionContactId to the selected contact's contactId (the attention person's ID)
        setAttentionContactId(contact.contactId);
        // Keep clientName as company name (Client = company, Attention = contact)
        setSelectedAttentionContactName(displayName);
        setAttentionSearchTerm(displayName);
        setShowAttentionDropdown(false);
    }, []);
    // Handle clearing attention contact selection
    const handleClearAttentionContact = useCallback(() => {
        setAttentionContactId("");
        // Reset clientName back to company name
        setClientName(selectedCompanyName);
        setSelectedAttentionContactName("");
        setAttentionSearchTerm("");
    }, [selectedCompanyName]);
    // Filtered contacts for attention dropdown based on search term
    // Contacts are already filtered by company via API call to getContacts({ companyName })
    // This ensures only contacts belonging to the selected company are shown
    const filteredAttentionContacts = useMemo(() => {
        // Only show contacts if a company is selected
        if (!selectedCompanyName) {
            return [];
        }
        // Contacts are already filtered by company via API call to getContacts({ companyName })
        // Filter by search term (name/email)
        if (!attentionSearchTerm || !attentionSearchTerm.trim()) {
            return companyContacts;
        }
        const searchLower = attentionSearchTerm.toLowerCase().trim();
        return companyContacts.filter((contact) => {
            const fullName = formatFullName(contact.firstName, contact.lastName).toLowerCase();
            const email = (contact.email || "").toLowerCase();
            return fullName.includes(searchLower) || email.includes(searchLower);
        });
    }, [companyContacts, attentionSearchTerm, selectedCompanyName]);
    // Calculate summary values from all work items
    const calculatedSummary = useMemo(() => {
        console.log("Recalculating summary...");
        let subTotal = 0;
        const categories = [
            "compliance",
            "service_trades",
            "flooring",
            "internal_demolition",
            "preliminary_cost",
        ];
        categories.forEach((category) => {
            const items = accordionData[category];
            items.forEach((accordionItem) => {
                if (accordionItem.sections) {
                    accordionItem.sections.forEach((section) => {
                        section.items.forEach((subItem) => {
                            if (subItem.inScope && subItem.totalCost) {
                                subTotal += parseCurrency(subItem.totalCost);
                            }
                        });
                    });
                }
            });
        });
        const marginAmount = buildersMarginEnabled
            ? (subTotal * buildersMarginPercentage) / 100
            : 0;
        const total = subTotal + marginAmount;
        // Get tenancy area from areaInfo for cost per m2
        const tenancyItem = areaInfo.find((item) => item.label.toLowerCase().includes("tenancy"));
        const tenancyArea = tenancyItem?.value || 0;
        const costPerM2 = tenancyArea > 0 ? total / tenancyArea : 0;
        const depositAmountRaw = depositEnabled
            ? (total * depositPercentage) / 100
            : 0;
        console.log("Summary calculated:", {
            subTotal,
            total,
            costPerM2,
            tenancyArea,
        });
        return {
            subTotal: formatCurrency(subTotal),
            buildersMarginAmount: formatCurrency(marginAmount),
            total: formatCurrency(total),
            costPerM2: formatCurrency(costPerM2),
            rawSubTotal: subTotal,
            rawTotal: total,
            rawCostPerM2: costPerM2,
            rawMarginAmount: marginAmount,
            depositAmount: formatCurrency(depositAmountRaw),
            rawDepositAmount: depositAmountRaw,
        };
    }, [
        accordionData,
        buildersMarginEnabled,
        buildersMarginPercentage,
        areaInfo,
        depositEnabled,
        depositPercentage,
    ]);
    const handleBack = () => {
        navigate(-1);
    };
    const handleViewQuote = () => {
        if (!estimateId)
            return;
        // Pass current location so back button can return here
        navigate(`${basePath}/estimating/${estimateId}/view`, {
            state: { from: location.pathname },
        });
    };
    const handleCreateJob = async () => {
        if (!quote)
            return;
        try {
            const newJob = await createJob(quote, { navigateToJob: false });
            // Reload quote data to get the linkedJobId
            await loadQuoteData();
            // Show success
            success(`Job #${newJob.jobNumber} created successfully!`);
        }
        catch (err) {
            console.error("Error creating job:", err);
        }
    };
    const handleEditJob = () => {
        if (!quote?.linkedJobId)
            return;
        navigate(`${basePath}/jobs/${quote.linkedJobId}/edit`);
    };
    const renderPageHeader = () => (_jsx(_Fragment, { children: _jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }) }));
    const handleAreaInfoChange = (index, value) => {
        const updated = [...areaInfo];
        updated[index] = { ...updated[index], value };
        console.log("Area info changed:", updated[index].label, "=", value);
        setAreaInfo(updated);
    };
    // Collect all work items from accordion data for saving
    const collectWorkItems = useCallback(() => {
        const workItems = [];
        let sortOrder = 0;
        Object.entries(accordionData).forEach(([category, items]) => {
            if (category === "summary")
                return;
            items.forEach((item) => {
                item.sections?.forEach((section) => {
                    section.items.forEach((subItem) => {
                        // Determine quantity based on sectionId for consistency
                        let quantity = undefined;
                        if (section.sectionId === "scissor-lift" && subItem.weeksQty) {
                            quantity = parseFloat(subItem.weeksQty);
                        }
                        else if (section.sectionId === "skip-hire" && subItem.numberQty) {
                            quantity = parseFloat(subItem.numberQty);
                        }
                        else if (subItem.qty) {
                            quantity = parseFloat(subItem.qty);
                        }
                        workItems.push({
                            category,
                            sectionId: section.sectionId,
                            sectionTitle: section.sectionTitle,
                            parentTitle: item.title,
                            itemId: subItem.id,
                            itemTitle: subItem.title,
                            inScope: subItem.inScope,
                            quantity: quantity,
                            weeksQty: subItem.weeksQty
                                ? parseFloat(subItem.weeksQty)
                                : undefined,
                            numberQty: subItem.numberQty
                                ? parseFloat(subItem.numberQty)
                                : undefined,
                            unitCost: subItem.unitCost
                                ? parseCurrency(subItem.unitCost)
                                : undefined,
                            totalCost: subItem.totalCost
                                ? parseCurrency(subItem.totalCost)
                                : undefined,
                            notes: subItem.notes,
                            isDropdown: subItem.isDropdown,
                            dropdownOptions: subItem.dropdownOptions,
                            selectedOption: subItem.selectedOption,
                            sortOrder: sortOrder++,
                        });
                    });
                });
            });
        });
        return workItems;
    }, [accordionData]);
    const handleSave = async () => {
        if (!quote)
            return;
        try {
            setSaving(true);
            setError(null);
            // Validate Attention field is mandatory
            if (!attentionContactId || attentionContactId.trim() === "") {
                setError("Please select an Attention contact");
                setSaving(false);
                return;
            }
            if (commencementDate && isWeekend(commencementDate)) {
                setError("Commencement date cannot fall on a weekend.");
                setSaving(false);
                return;
            }
            // Automatically change status from "draft" to "inprogress" when saving changes
            let statusToSave = quoteStatus;
            if (quoteStatus === "draft") {
                statusToSave = "inprogress";
                setQuoteStatus("inprogress"); // Update local state
            }
            const lineItems = collectWorkItems();
            // Build address from site fields
            const addressParts = [];
            if (linkedSiteAddress)
                addressParts.push(linkedSiteAddress);
            if (linkedSiteSuburb)
                addressParts.push(linkedSiteSuburb);
            if (linkedSiteState)
                addressParts.push(linkedSiteState);
            if (linkedSitePostcode)
                addressParts.push(linkedSitePostcode);
            const concatenatedAddress = addressParts.length > 0 ? addressParts.join(", ") : undefined;
            const updateData = {
                // contactId should always be the company's contact ID, not the attention contact ID
                contactId: selectedContactId || undefined,
                siteId: siteId || undefined,
                title: quoteName,
                description: quoteDescription,
                status: statusToSave,
                defitType: defitType,
                commencementDate: commencementDate || undefined,
                commencementTime: commencementTime || undefined,
                completionDate: completionDate || undefined,
                completionTime: completionTime || undefined,
                projectDuration: projectDuration
                    ? parseInt(projectDuration, 10)
                    : undefined,
                areaInfo,
                lineItems,
                subTotal: calculatedSummary.rawSubTotal,
                totalAmount: calculatedSummary.rawTotal,
                costPerM2: calculatedSummary.rawCostPerM2,
                buildersMarginEnabled,
                buildersMarginPercentage,
                buildersMarginAmount: calculatedSummary.rawMarginAmount,
                includeNewClientPaymentTerms: includeNewClientTerms,
                depositEnabled,
                depositPercentage: depositEnabled ? depositPercentage : undefined,
                attentionContactId: attentionContactId || undefined,
                // Persist company name as Client (not attention contact's name)
                clientName: selectedCompanyName || clientName || undefined,
                address: concatenatedAddress,
                shoppingCentreId: shoppingCentreId.trim() || "",
            };
            console.log("Saving quote with data:", updateData);
            await quotesService.updateQuote(quote.id, updateData);
            // Reload data after save
            await loadQuoteData();
            success("Estimate saved successfully!");
        }
        catch (err) {
            const errorMessage = err?.response?.data?.message || err?.message || "Failed to save quote";
            setError(errorMessage);
            console.error("Error saving quote:", err);
            console.error("Error details:", err?.response?.data);
        }
        finally {
            setSaving(false);
        }
    };
    // Handle status change from action bar dropdown
    const handleStatusChange = async (newStatus) => {
        if (!estimateId)
            return;
        // Update local state immediately for UI responsiveness
        setQuoteStatus(newStatus);
        try {
            await quotesService.updateQuoteStatus(estimateId, newStatus);
            await loadQuoteData();
            success("Status updated successfully!");
        }
        catch (err) {
            console.error("Failed to update status:", err);
            setError("Failed to update status");
            // Revert to original status on error
            if (quote?.status) {
                setQuoteStatus(getQuoteStatusForDisplay(quote.status));
            }
        }
    };
    // Accordion handlers
    const toggleAccordion = (id) => {
        setExpandedAccordions((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            }
            else {
                newSet.add(id);
            }
            return newSet;
        });
    };
    const handleExpandAll = () => {
        const currentItems = accordionData[activeWorkCategory];
        const allIds = currentItems.map((item) => item.id);
        setExpandedAccordions(new Set(allIds));
    };
    const handleCollapseAll = () => {
        setExpandedAccordions(new Set());
    };
    const handleRefreshPrices = useCallback(async () => {
        setRefreshingPrices(true);
        try {
            const response = await sitesService.getDefaultWorkItems();
            const freshCostParams = response.data;
            setCostParameters(freshCostParams);
            setAccordionData((prev) => {
                const newData = JSON.parse(JSON.stringify(prev));
                freshCostParams.forEach((cp) => {
                    const category = cp.category;
                    if (!newData[category])
                        return;
                    const accordionItem = newData[category].find((item) => item.sections?.some((section) => {
                        if (section.sectionId !== cp.sectionId)
                            return false;
                        if (cp.sectionId === "joinery" &&
                            cp.sectionTitle &&
                            section.sectionTitle !== cp.sectionTitle) {
                            return false;
                        }
                        return true;
                    }));
                    if (accordionItem?.sections) {
                        for (const section of accordionItem.sections) {
                            const sectionMatches = section.sectionId === cp.sectionId &&
                                (cp.sectionId !== "joinery" ||
                                    !cp.sectionTitle ||
                                    section.sectionTitle === cp.sectionTitle);
                            if (!sectionMatches)
                                continue;
                            const subItem = section.items.find((i) => i.id === cp.itemId);
                            if (subItem) {
                                subItem.defaultUnitCost =
                                    cp.defaultUnitCost ?? cp.unitCost ?? null;
                                subItem.unitCost =
                                    cp.unitCost != null ? `${cp.unitCost}` : subItem.unitCost;
                                break;
                            }
                        }
                    }
                });
                return newData;
            });
            setRefreshTrigger((t) => t + 1);
            success("Prices refreshed. Click Save Estimate to persist changes.");
        }
        catch (err) {
            console.error("Error refreshing prices:", err);
            showErrorToast("Failed to refresh prices.");
        }
        finally {
            setRefreshingPrices(false);
        }
    }, [success, showErrorToast]);
    // Helper function to recalculate all items in a section (handles After Hours correctly)
    const recalculateSectionItems = (items, sectionId, category) => {
        const isAfterHoursItem = (itemTitle) => itemTitle.toLowerCase().includes("after hours");
        // Check After Hours status directly from items array (not from prevData which has stale state)
        const afterHoursItem = items.find((item) => item.title.toLowerCase().includes("after hours"));
        const afterHoursEnabled = afterHoursItem?.inScope || false;
        // Extract After Hours multiplier from the item's unitCost (defaults to 1.3 if not set)
        const afterHoursMultiplier = afterHoursEnabled && afterHoursItem?.unitCost
            ? parseFloat(afterHoursItem.unitCost.toString().replace(/[^0-9.-]/g, "")) || 1.3
            : undefined;
        const shopfrontSettings = getShopfrontSettingsFromItems(items, costParameters);
        const updatedItems = [...items];
        // Find Float value from Cost Settings (costParameters) for "19" Electric calculation
        const isScissorLiftSection = sectionId === "scissor-lift";
        const floatCostParam = isScissorLiftSection
            ? costParameters.find((cp) => cp.category === "preliminary_cost" &&
                cp.sectionId === "scissor-lift" &&
                cp.itemTitle?.toLowerCase().includes("float"))
            : null;
        const floatValue = floatCostParam?.unitCost ?? undefined;
        // For shopfronts section, process shopfront-1 and shopfront-2 first to ensure they're recalculated
        // before the OVER 3.6m item (which depends on their totals)
        if (sectionId === "shopfronts") {
            updatedItems.forEach((subItem, subItemIndex) => {
                const isStandardGlazed = subItem.id === "shopfront-1" ||
                    subItem.title.toLowerCase().includes("standard glazed") ||
                    subItem.title.toLowerCase().includes("standard");
                const isHeavyDuty = subItem.id === "shopfront-2" ||
                    subItem.title.toLowerCase().includes("heavy duty") ||
                    subItem.title.toLowerCase().includes("heavy");
                // Recalculate shopfront-1 and shopfront-2 if they're inScope and have unitCost
                if ((isStandardGlazed || isHeavyDuty) &&
                    subItem.inScope &&
                    subItem.unitCost) {
                    const unitCost = parseCurrency(subItem.unitCost);
                    if (unitCost > 0) {
                        // Calculate base total cost (without height factor)
                        const calculationResult = calculateTotalCost({
                            itemId: subItem.id,
                            sectionId,
                            category,
                            title: subItem.title,
                            unitCost,
                            quantity: subItem.qty ? parseFloat(subItem.qty) : undefined,
                            areaInfo,
                            afterHoursEnabled,
                            afterHoursMultiplier,
                        });
                        // Apply height factor only if toggle is ON
                        let finalTotal = calculationResult.totalCost;
                        if (shopfrontSettings.heightEnabled) {
                            if (isStandardGlazed) {
                                finalTotal *= shopfrontSettings.stdHeightFactor;
                            }
                            else if (isHeavyDuty) {
                                finalTotal *= shopfrontSettings.hvyHeightFactor;
                            }
                        }
                        updatedItems[subItemIndex] = {
                            ...updatedItems[subItemIndex],
                            totalCost: formatCurrency(finalTotal),
                        };
                    }
                }
            });
        }
        // First pass: Calculate all non-After-Hours items
        updatedItems.forEach((subItem, subItemIndex) => {
            if (subItem.inScope && !isAfterHoursItem(subItem.title)) {
                const isShopfrontOver36m = subItem.title
                    .toLowerCase()
                    .includes("over 3.6m");
                // For OVER 3.6m item, calculate even without unit cost
                if (isShopfrontOver36m && sectionId === "shopfronts") {
                    const shopfrontItemsTotal = getShopfrontItemsTotal(updatedItems, shopfrontSettings);
                    const calculationResult = calculateTotalCost({
                        itemId: subItem.id,
                        sectionId,
                        category,
                        title: subItem.title,
                        unitCost: 0, // Not used for OVER 3.6m
                        areaInfo,
                        afterHoursEnabled,
                        afterHoursMultiplier,
                        shopfrontItemsTotal,
                    });
                    updatedItems[subItemIndex] = {
                        ...updatedItems[subItemIndex],
                        totalCost: formatCurrency(calculationResult.totalCost),
                    };
                }
                else if (subItem.unitCost) {
                    // For other items, require unit cost
                    const unitCost = parseCurrency(subItem.unitCost);
                    if (unitCost > 0) {
                        // Skip shopfront-1 and shopfront-2 if we already processed them above
                        const isStandardGlazed = subItem.id === "shopfront-1" ||
                            subItem.title.toLowerCase().includes("standard glazed") ||
                            subItem.title.toLowerCase().includes("standard");
                        const isHeavyDuty = subItem.id === "shopfront-2" ||
                            subItem.title.toLowerCase().includes("heavy duty") ||
                            subItem.title.toLowerCase().includes("heavy");
                        // If this is shopfront-1 or shopfront-2 and we're in shopfronts section, skip (already processed)
                        if (sectionId === "shopfronts" &&
                            (isStandardGlazed || isHeavyDuty)) {
                            // Already processed above, skip
                        }
                        else {
                            // Check if this is "19" Electric in scissor-lift section
                            const is19Electric = isScissorLiftSection &&
                                subItem.title.toLowerCase().includes("19") &&
                                subItem.title.toLowerCase().includes("electric");
                            // Calculate base total cost (without height factor)
                            const calculationResult = calculateTotalCost({
                                itemId: subItem.id,
                                sectionId,
                                category,
                                title: subItem.title,
                                unitCost,
                                quantity: subItem.qty ? parseFloat(subItem.qty) : undefined,
                                weeksQty: subItem.weeksQty
                                    ? parseFloat(subItem.weeksQty)
                                    : undefined,
                                numberQty: subItem.numberQty
                                    ? parseFloat(subItem.numberQty)
                                    : undefined,
                                areaInfo,
                                afterHoursEnabled,
                                afterHoursMultiplier,
                                floatValue: is19Electric ? floatValue : undefined,
                            });
                            // Apply height factor for shopfront items if toggle is ON
                            let finalTotal = calculationResult.totalCost;
                            if (sectionId === "shopfronts" &&
                                shopfrontSettings.heightEnabled) {
                                if (isStandardGlazed) {
                                    finalTotal *= shopfrontSettings.stdHeightFactor;
                                }
                                else if (isHeavyDuty) {
                                    finalTotal *= shopfrontSettings.hvyHeightFactor;
                                }
                            }
                            updatedItems[subItemIndex] = {
                                ...updatedItems[subItemIndex],
                                totalCost: formatCurrency(finalTotal),
                            };
                        }
                    }
                }
            }
            else if (!subItem.inScope) {
                // Clear total cost when not in scope (but keep for After Hours items)
                if (!isAfterHoursItem(subItem.title)) {
                    updatedItems[subItemIndex] = {
                        ...updatedItems[subItemIndex],
                        totalCost: "",
                    };
                }
            }
        });
        // Second pass: Calculate After Hours items using updated totals from first pass
        updatedItems.forEach((subItem, subItemIndex) => {
            if (subItem.inScope &&
                subItem.unitCost &&
                isAfterHoursItem(subItem.title)) {
                // For ALL After Hours items, calculate normally using unit cost from cost settings
                // NO 30% calculation - all After Hours items use their unit cost
                const unitCost = parseCurrency(subItem.unitCost);
                if (unitCost > 0) {
                    // Calculate shopfront items total for OVER 3.6m item (if applicable)
                    const isShopfrontOver36m = subItem.title
                        .toLowerCase()
                        .includes("over 3.6m");
                    const shopfrontItemsTotal = isShopfrontOver36m && sectionId === "shopfronts"
                        ? getShopfrontItemsTotal(updatedItems, shopfrontSettings)
                        : undefined;
                    const calculationResult = calculateTotalCost({
                        itemId: subItem.id,
                        sectionId,
                        category,
                        title: subItem.title,
                        unitCost, // Unit cost from cost settings
                        areaInfo,
                        afterHoursEnabled: false, // After Hours item itself doesn't use the factor
                        shopfrontItemsTotal,
                    });
                    updatedItems[subItemIndex] = {
                        ...updatedItems[subItemIndex],
                        totalCost: formatCurrency(calculationResult.totalCost),
                    };
                }
            }
            else if (!subItem.inScope && isAfterHoursItem(subItem.title)) {
                updatedItems[subItemIndex] = {
                    ...updatedItems[subItemIndex],
                    totalCost: "",
                };
            }
        });
        return updatedItems;
    };
    const toggleSubItemScope = (itemId, sectionIndex, subItemId) => {
        setAccordionData((prevData) => {
            const newData = { ...prevData };
            const categoryItems = [...newData[activeWorkCategory]];
            const itemIndex = categoryItems.findIndex((item) => item.id === itemId);
            if (itemIndex === -1)
                return prevData;
            const item = { ...categoryItems[itemIndex] };
            if (!item.sections)
                return prevData;
            const sections = [...item.sections];
            const section = { ...sections[sectionIndex] };
            const items = [...section.items];
            const subItemIndex = items.findIndex((sub) => sub.id === subItemId);
            if (subItemIndex === -1)
                return prevData;
            const toggledItem = items[subItemIndex];
            const newInScope = !toggledItem.inScope;
            const isAfterHoursItem = toggledItem.title
                .toLowerCase()
                .includes("after hours");
            // Preserve edited unitCost for After Hours items when toggling
            // Only set default from Cost Settings if unitCost is empty
            let updatedUnitCost = toggledItem.unitCost;
            if (isAfterHoursItem) {
                if (newInScope) {
                    // Toggling to Yes - only set default if unitCost is empty
                    if (!toggledItem.unitCost || toggledItem.unitCost.trim() === "") {
                        // First try to use defaultUnitCost if available
                        if (toggledItem.defaultUnitCost != null) {
                            updatedUnitCost = `${toggledItem.defaultUnitCost}`;
                        }
                        // If not available, look up from costParameters
                        else {
                            const costParam = costParameters.find((cp) => cp.sectionId === section.sectionId &&
                                cp.itemTitle
                                    ?.toLowerCase()
                                    .includes("after hours works required"));
                            if (costParam?.unitCost != null) {
                                updatedUnitCost = `${costParam.unitCost}`;
                                // Also store it as defaultUnitCost for future use
                                toggledItem.defaultUnitCost = costParam.unitCost;
                            }
                        }
                    }
                    // If unitCost already has a value, preserve it (don't overwrite edited values)
                }
                else {
                    // Toggling to No - preserve the value (don't clear it)
                    // This allows admins to keep their edited rate even when toggled off
                    updatedUnitCost = toggledItem.unitCost || "";
                }
            }
            items[subItemIndex] = {
                ...toggledItem,
                inScope: newInScope,
                unitCost: updatedUnitCost,
            };
            // Check if this is the shopfront OVER 3.6m toggle
            const isShopfrontOver36mToggle = subItemId === "shopfront-3a" ||
                toggledItem.title?.toLowerCase().includes("over 3.6m");
            // Handle shopfront-3a toggle: load shopfront-3 and shopfront-4 defaults when toggled YES
            if (isShopfrontOver36mToggle && section.sectionId === "shopfronts") {
                if (newInScope) {
                    // Toggling to YES - load shopfront-3 and shopfront-4 defaults from costsettings if not already stored
                    const shopfront3Item = items.find((item) => item.id === "shopfront-3");
                    const shopfront4Item = items.find((item) => item.id === "shopfront-4");
                    // Load shopfront-3 default if not already stored
                    if (shopfront3Item &&
                        (!shopfront3Item.unitCost || shopfront3Item.unitCost.trim() === "")) {
                        const shopfront3CostParam = costParameters.find((cp) => cp.itemId === "shopfront-3" ||
                            cp.itemTitle
                                ?.toLowerCase()
                                .includes("shopfront height factor std"));
                        if (shopfront3CostParam?.unitCost != null) {
                            const shopfront3Index = items.findIndex((item) => item.id === "shopfront-3");
                            if (shopfront3Index !== -1) {
                                items[shopfront3Index] = {
                                    ...items[shopfront3Index],
                                    unitCost: `${shopfront3CostParam.unitCost}`,
                                };
                            }
                        }
                    }
                    // Load shopfront-4 default if not already stored
                    if (shopfront4Item &&
                        (!shopfront4Item.unitCost || shopfront4Item.unitCost.trim() === "")) {
                        const shopfront4CostParam = costParameters.find((cp) => cp.itemId === "shopfront-4" ||
                            cp.itemTitle
                                ?.toLowerCase()
                                .includes("shopfront height factor hvy"));
                        if (shopfront4CostParam?.unitCost != null) {
                            const shopfront4Index = items.findIndex((item) => item.id === "shopfront-4");
                            if (shopfront4Index !== -1) {
                                items[shopfront4Index] = {
                                    ...items[shopfront4Index],
                                    unitCost: `${shopfront4CostParam.unitCost}`,
                                };
                            }
                        }
                    }
                }
            }
            // Always recalculate section items when any toggle changes
            // This is especially important when the shopfront OVER 3.6m toggle (shopfront-3a) changes,
            // as it ensures shopfront-1 and shopfront-2 are recalculated with correct height factor.
            // Also handles After Hours items correctly.
            const recalculatedItems = recalculateSectionItems(items, section.sectionId, activeWorkCategory);
            section.items = recalculatedItems;
            sections[sectionIndex] = section;
            item.sections = sections;
            const isScissorLiftToggle = subItemId === "scissor-1";
            // If the scissor-lift toggle changed, also recalculate the scissor-lift section
            // so that the \"19\" Electric item is always up to date (including float value)
            if (isScissorLiftToggle && section.sectionId === "scissor-lift") {
                const scissorLiftItemIndex = categoryItems.findIndex((catItem) => catItem.sections?.some((s) => s.sectionId === "scissor-lift"));
                if (scissorLiftItemIndex !== -1) {
                    const scissorLiftItem = { ...categoryItems[scissorLiftItemIndex] };
                    if (scissorLiftItem.sections) {
                        const scissorLiftSections = [...scissorLiftItem.sections];
                        const scissorLiftSectionIndex = scissorLiftSections.findIndex((s) => s.sectionId === "scissor-lift");
                        if (scissorLiftSectionIndex !== -1) {
                            const scissorLiftSection = {
                                ...scissorLiftSections[scissorLiftSectionIndex],
                            };
                            const scissorLiftItems = [...scissorLiftSection.items];
                            const recalculatedScissorLiftItems = recalculateSectionItems(scissorLiftItems, "scissor-lift", activeWorkCategory);
                            scissorLiftSection.items = recalculatedScissorLiftItems;
                            scissorLiftSections[scissorLiftSectionIndex] = scissorLiftSection;
                            scissorLiftItem.sections = scissorLiftSections;
                            categoryItems[scissorLiftItemIndex] = scissorLiftItem;
                        }
                    }
                }
            }
            // If the OVER 3.6m shopfront toggle changed, also recalculate the scissor-lift section
            // so that the \"19\" Electric item is always up to date (including float value)
            if (isShopfrontOver36mToggle && section.sectionId === "shopfronts") {
                const scissorLiftItemIndex = categoryItems.findIndex((catItem) => catItem.sections?.some((s) => s.sectionId === "scissor-lift"));
                if (scissorLiftItemIndex !== -1) {
                    const scissorLiftItem = { ...categoryItems[scissorLiftItemIndex] };
                    if (scissorLiftItem.sections) {
                        const scissorLiftSections = [...scissorLiftItem.sections];
                        const scissorLiftSectionIndex = scissorLiftSections.findIndex((s) => s.sectionId === "scissor-lift");
                        if (scissorLiftSectionIndex !== -1) {
                            const scissorLiftSection = {
                                ...scissorLiftSections[scissorLiftSectionIndex],
                            };
                            const scissorLiftItems = [...scissorLiftSection.items];
                            const recalculatedScissorLiftItems = recalculateSectionItems(scissorLiftItems, "scissor-lift", activeWorkCategory);
                            scissorLiftSection.items = recalculatedScissorLiftItems;
                            scissorLiftSections[scissorLiftSectionIndex] = scissorLiftSection;
                            scissorLiftItem.sections = scissorLiftSections;
                            categoryItems[scissorLiftItemIndex] = scissorLiftItem;
                        }
                    }
                }
            }
            categoryItems[itemIndex] = item;
            newData[activeWorkCategory] = categoryItems;
            return newData;
        });
    };
    const updateSubItemField = (itemId, sectionIndex, subItemId, field, value) => {
        setAccordionData((prevData) => {
            const newData = { ...prevData };
            const categoryItems = [...newData[activeWorkCategory]];
            const itemIndex = categoryItems.findIndex((item) => item.id === itemId);
            if (itemIndex === -1)
                return prevData;
            const item = { ...categoryItems[itemIndex] };
            if (!item.sections)
                return prevData;
            const sections = [...item.sections];
            const section = { ...sections[sectionIndex] };
            const items = [...section.items];
            const subItemIndex = items.findIndex((sub) => sub.id === subItemId);
            if (subItemIndex === -1)
                return prevData;
            // Update the field
            const updatedSubItem = { ...items[subItemIndex], [field]: value };
            items[subItemIndex] = updatedSubItem;
            // If updating shopfront-3 or shopfront-4 unitCost, trigger recalculation of dependent items
            const isShopfront3Or4 = subItemId === "shopfront-3" || subItemId === "shopfront-4";
            if (isShopfront3Or4 &&
                field === "unitCost" &&
                section.sectionId === "shopfronts") {
                // Recalculate shopfront-1 and shopfront-2 with new height factors
                // The items array already has the updated shopfront-3/4 value (set on line 2431)
                const recalculatedItems = recalculateSectionItems(items, section.sectionId, activeWorkCategory);
                // Replace items with recalculated items, but preserve our updated shopfront-3/4 value
                recalculatedItems.forEach((recalculatedItem, index) => {
                    if (recalculatedItem.id === subItemId) {
                        // Preserve the updated value we just set (don't let recalculation overwrite it)
                        items[index] = updatedSubItem;
                    }
                    else {
                        items[index] = recalculatedItem;
                    }
                });
                section.items = items;
                sections[sectionIndex] = section;
                item.sections = sections;
                // Also recalculate scissor-lift section if shopfront-3a is enabled
                const shopfront3aItem = items.find((item) => item.id === "shopfront-3a");
                if (shopfront3aItem?.inScope) {
                    const scissorLiftItemIndex = categoryItems.findIndex((catItem) => catItem.sections?.some((s) => s.sectionId === "scissor-lift"));
                    if (scissorLiftItemIndex !== -1) {
                        const scissorLiftItem = { ...categoryItems[scissorLiftItemIndex] };
                        if (scissorLiftItem.sections) {
                            const scissorLiftSections = [...scissorLiftItem.sections];
                            const scissorLiftSectionIndex = scissorLiftSections.findIndex((s) => s.sectionId === "scissor-lift");
                            if (scissorLiftSectionIndex !== -1) {
                                const scissorLiftSection = {
                                    ...scissorLiftSections[scissorLiftSectionIndex],
                                };
                                const scissorLiftItems = [...scissorLiftSection.items];
                                const recalculatedScissorLiftItems = recalculateSectionItems(scissorLiftItems, "scissor-lift", activeWorkCategory);
                                scissorLiftSection.items = recalculatedScissorLiftItems;
                                scissorLiftSections[scissorLiftSectionIndex] =
                                    scissorLiftSection;
                                scissorLiftItem.sections = scissorLiftSections;
                                categoryItems[scissorLiftItemIndex] = scissorLiftItem;
                            }
                        }
                    }
                }
                categoryItems[itemIndex] = item;
                newData[activeWorkCategory] = categoryItems;
                return newData;
            }
            // If updating shopfront-1 or shopfront-2 totalCost, recalculate OVER 3.6m item
            if (field === "totalCost" &&
                section.sectionId === "shopfronts" &&
                (updatedSubItem.id === "shopfront-1" ||
                    updatedSubItem.id === "shopfront-2")) {
                const over36mItem = items.find((item) => item.title.toLowerCase().includes("over 3.6m") && item.inScope);
                if (over36mItem) {
                    const afterHoursEnabled = getSectionAfterHoursStatus(prevData, activeWorkCategory, section.sectionId);
                    const shopfrontSettings = getShopfrontSettingsFromItems(items, costParameters);
                    const shopfrontItemsTotal = getShopfrontItemsTotal(items, shopfrontSettings);
                    // Extract After Hours multiplier from the item's unitCost
                    const afterHoursItem = items.find((item) => item.title.toLowerCase().includes("after hours"));
                    const afterHoursMultiplier = afterHoursEnabled && afterHoursItem?.unitCost
                        ? parseFloat(afterHoursItem.unitCost.toString().replace(/[^0-9.-]/g, "")) || 1.3
                        : undefined;
                    const calculationResult = calculateTotalCost({
                        itemId: over36mItem.id,
                        sectionId: section.sectionId,
                        category: activeWorkCategory,
                        title: over36mItem.title,
                        unitCost: 0,
                        areaInfo,
                        afterHoursEnabled,
                        afterHoursMultiplier,
                        shopfrontItemsTotal,
                    });
                    const over36mIndex = items.findIndex((item) => item.id === over36mItem.id);
                    if (over36mIndex !== -1) {
                        items[over36mIndex] = {
                            ...over36mItem,
                            totalCost: formatCurrency(calculationResult.totalCost),
                        };
                    }
                }
            }
            // Recalculate all items in the section (handles After Hours correctly)
            const recalculatedItems = recalculateSectionItems(items, section.sectionId, activeWorkCategory);
            section.items = recalculatedItems;
            sections[sectionIndex] = section;
            item.sections = sections;
            categoryItems[itemIndex] = item;
            newData[activeWorkCategory] = categoryItems;
            return newData;
        });
    };
    // Render Summary tab content
    const renderSummaryContent = () => (_jsxs("div", { className: styles.summaryContainer, children: [_jsxs("div", { className: styles.summaryRow, children: [_jsx("div", { className: styles.summaryLabel, children: "Sub Total" }), _jsx("div", { className: styles.summaryValue, children: calculatedSummary.subTotal })] }), _jsxs("div", { className: styles.summaryMarginRow, children: [_jsxs("div", { className: styles.summaryMarginLeft, children: [_jsx("span", { className: styles.summaryMarginLabel, children: "Builders Preliminaries & Margin" }), _jsx("button", { className: `${styles.toggleButton} ${buildersMarginEnabled ? styles.yes : styles.no}`, onClick: () => setBuildersMarginEnabled(!buildersMarginEnabled), children: buildersMarginEnabled ? "YES" : "NO" })] }), _jsxs("div", { className: styles.summaryMarginRight, children: [_jsx("input", { type: "number", className: styles.marginPercentInput, value: buildersMarginPercentage, onChange: (e) => setBuildersMarginPercentage(Number(e.target.value) || 0), disabled: !buildersMarginEnabled, min: "0", max: "100" }), _jsx("span", { className: styles.percentSign, children: "%" }), _jsx("span", { className: styles.summaryMarginAmount, children: calculatedSummary.buildersMarginAmount })] })] }), _jsxs("div", { className: styles.summaryRow, children: [_jsx("div", { className: styles.summaryLabel, children: "Total" }), _jsx("div", { className: styles.summaryValue, children: calculatedSummary.total })] }), _jsxs("div", { className: styles.summaryRow, children: [_jsx("div", { className: styles.summaryLabel, children: "Cost per m2" }), _jsx("div", { className: styles.summaryValue, children: calculatedSummary.costPerM2 })] }), _jsxs("div", { className: styles.paymentTermsSection, children: [_jsx("h4", { className: styles.paymentTermsTitle, children: "PAYMENT TERMS" }), _jsxs("div", { className: styles.paymentTermsRow, children: [_jsx("span", { className: styles.paymentTermsLabel, children: "Include new client payment terms" }), _jsx("button", { className: `${styles.toggleButton} ${includeNewClientTerms ? styles.yes : styles.no}`, onClick: () => setIncludeNewClientTerms(!includeNewClientTerms), children: includeNewClientTerms ? "YES" : "NO" })] }), includeNewClientTerms && (_jsxs("div", { className: styles.depositSection, children: [_jsx("h5", { className: styles.depositTitle, children: "Deposit" }), _jsxs("div", { className: styles.depositRow, children: [_jsx(Checkbox, { checked: depositEnabled, onChange: (checked) => setDepositEnabled(checked), label: "Amount", size: "md" }), _jsxs("div", { className: styles.depositInputWrapper, children: [_jsx("input", { type: "number", className: styles.depositInput, value: depositPercentage, onChange: (e) => setDepositPercentage(Number(e.target.value) || 0), disabled: !depositEnabled, min: "0", max: "100" }), _jsx("span", { className: styles.percentSign, children: "%" })] })] }), _jsxs("div", { className: styles.depositAmountRow, children: [_jsxs("span", { className: styles.depositAmountLabel, children: ["Deposit amount (", depositPercentage, "%)"] }), _jsx("span", { className: styles.depositAmountValue, children: calculatedSummary.depositAmount })] })] }))] }), _jsx("div", { className: styles.tabActionButtons, children: _jsx("button", { className: styles.saveEstimateButton, onClick: handleSave, disabled: saving, children: saving ? "Saving..." : "Save Estimate" }) })] }));
    // Render accordion content for work categories
    const renderAccordionContent = () => {
        if (activeWorkCategory === "summary") {
            return renderSummaryContent();
        }
        const items = accordionData[activeWorkCategory];
        return (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.accordionList, children: items.map((item) => (_jsxs("div", { className: styles.accordionItem, children: [_jsx("button", { className: `${styles.accordionHeader} ${expandedAccordions.has(item.id) ? styles.expanded : ""}`, onClick: () => toggleAccordion(item.id), children: _jsxs("div", { className: styles.accordionTitleSection, children: [_jsx("svg", { className: styles.accordionChevron, width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M6 9l6 6 6-6" }) }), _jsx("span", { className: styles.accordionTitle, children: item.title })] }) }), expandedAccordions.has(item.id) && (_jsx("div", { className: styles.accordionBody, children: item.sections ? (_jsx("div", { className: styles.subItemsTable, children: item.sections.map((section, sectionIndex) => (_jsxs("div", { className: styles.sectionBlock, children: [section.sectionTitle && (_jsx("div", { className: styles.sectionTitleRow, children: _jsx("span", { className: styles.sectionTitleText, children: section.sectionTitle }) })), section.isDropdownSection ? (_jsxs("div", { className: styles.tableHeaderDropdown, children: [_jsx("div", { className: styles.tableHeaderCell }), _jsx("div", { className: styles.tableHeaderCell, children: "Total Cost" }), _jsx("div", { className: styles.tableHeaderCell, children: "Notes" })] })) : (_jsxs("div", { className: section.showQty ||
                                                    section.showWeeksQty ||
                                                    section.showNumberQty
                                                    ? styles.tableHeaderWithQty
                                                    : styles.tableHeader, children: [_jsx("div", { className: styles.tableHeaderCell }), _jsx("div", { className: styles.tableHeaderCell, children: "In Scope" }), section.showQty && (_jsx("div", { className: styles.tableHeaderCell, children: "Qty" })), section.showWeeksQty && (_jsx("div", { className: styles.tableHeaderCell, children: "# of Weeks" })), section.showNumberQty && (_jsx("div", { className: styles.tableHeaderCell, children: "No." })), _jsx("div", { className: styles.tableHeaderCell, children: "Unit Cost" }), _jsx("div", { className: styles.tableHeaderCell, children: "Total Cost" }), _jsx("div", { className: styles.tableHeaderCell, children: "Notes" })] })), section.items
                                                .filter((subItem) => {
                                                // Filter out shopfront-3 and shopfront-4 - they don't have rows
                                                if (subItem.id === "shopfront-3" ||
                                                    subItem.id === "shopfront-4") {
                                                    return false;
                                                }
                                                const title = subItem.title.toLowerCase();
                                                return (!title.includes("shopfront height factor std") &&
                                                    !title.includes("shopfront height factor hvy"));
                                            })
                                                .map((subItem) => section.isDropdownSection ? (_jsxs("div", { className: styles.tableRowDropdown, children: [_jsx("div", { className: styles.tableCellDropdown, children: _jsxs("div", { className: styles.dropdownFieldGroup, children: [_jsx("label", { className: styles.dropdownLabel, children: subItem.title }), _jsx("select", { className: styles.dropdownSelect, value: subItem.selectedOption || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "selectedOption", e.target.value), children: subItem.dropdownOptions?.map((option) => (_jsx("option", { value: option, children: option }, option))) })] }) }), _jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.costInput, value: subItem.totalCost || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "totalCost", e.target.value), readOnly: !canEditPrices }) }), _jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.notesInput, value: subItem.notes || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "notes", e.target.value) }) })] }, subItem.id)) : (_jsxs("div", { className: section.showQty ||
                                                    section.showWeeksQty ||
                                                    section.showNumberQty
                                                    ? styles.tableRowWithQty
                                                    : styles.tableRow, children: [_jsx("div", { className: styles.tableCell, children: _jsx("span", { className: styles.subItemTitle, children: subItem.title }) }), _jsx("div", { className: styles.tableCell, children: _jsx("button", { className: `${styles.toggleButton} ${subItem.inScope ? styles.yes : styles.no}`, onClick: () => toggleSubItemScope(item.id, sectionIndex, subItem.id), children: subItem.inScope ? "YES" : "NO" }) }), section.showQty && (_jsx("div", { className: styles.tableCell, children: !subItem.hideQty && (_jsx("input", { type: "text", className: styles.qtyInput, value: subItem.qty || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "qty", e.target.value) })) })), section.showWeeksQty && (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.qtyInput, value: subItem.weeksQty || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "weeksQty", e.target.value) }) })), section.showNumberQty && (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.qtyInput, value: subItem.numberQty || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "numberQty", e.target.value) }) })), (() => {
                                                        const isShopfrontOver36m = subItem.id === "shopfront-3a" ||
                                                            subItem.title
                                                                .toLowerCase()
                                                                .includes("over 3.6m");
                                                        if (isShopfrontOver36m) {
                                                            // For shopfront-3a, display shopfront-3's unitCost in the unitCost field
                                                            // Get shopfront-3 item from the section
                                                            const shopfront3Item = section.items.find((item) => item.id === "shopfront-3");
                                                            const shopfront3Value = shopfront3Item?.unitCost || "";
                                                            // Only show input if toggle is YES
                                                            if (subItem.inScope) {
                                                                return (_jsxs("div", { className: `${styles.tableCell} ${styles.tableCellWithLabel}`, children: [_jsx("label", { children: shopfront3Item?.title }), _jsx("input", { type: "text", className: styles.costInput, value: shopfront3Value, onChange: (e) => {
                                                                                // Update shopfront-3's unitCost when user edits
                                                                                const shopfront3Index = section.items.findIndex((item) => item.id === "shopfront-3");
                                                                                if (shopfront3Index !== -1) {
                                                                                    const normalizedValue = e.target.value
                                                                                        .replace(/,/g, ".")
                                                                                        .replace(/[^0-9.-]/g, "");
                                                                                    const parts = normalizedValue.split(".");
                                                                                    const finalValue = parts.length > 2
                                                                                        ? parts[0] +
                                                                                            "." +
                                                                                            parts.slice(1).join("")
                                                                                        : normalizedValue;
                                                                                    updateSubItemField(item.id, sectionIndex, "shopfront-3", "unitCost", finalValue);
                                                                                }
                                                                            }, readOnly: !canEditPrices })] }));
                                                            }
                                                            else {
                                                                return (_jsx("div", { className: styles.tableCell }));
                                                            }
                                                        }
                                                        return (_jsx("div", { className: styles.tableCell, children: (() => {
                                                                const isDecimalInputItem = (title) => {
                                                                    const lower = title.toLowerCase();
                                                                    return (lower.includes("shopfront height factor std") ||
                                                                        lower.includes("shopfront height factor hvy") ||
                                                                        lower.includes("after hours"));
                                                                };
                                                                const isDecimalItem = isDecimalInputItem(subItem.title);
                                                                // For decimal items, preserve raw string value to allow decimal input
                                                                // Only format when displaying a finalized number (not during active editing)
                                                                const displayValue = isDecimalItem
                                                                    ? subItem.unitCost || ""
                                                                    : subItem.unitCost || "";
                                                                return (_jsx("input", { type: "text", className: styles.costInput, value: displayValue, onChange: (e) => {
                                                                        let normalizedValue = e.target.value;
                                                                        if (isDecimalItem) {
                                                                            // Allow decimal input - replace comma with period, but preserve periods
                                                                            normalizedValue =
                                                                                normalizedValue.replace(/,/g, ".");
                                                                            // Allow only numbers, one decimal point, and negative sign
                                                                            normalizedValue =
                                                                                normalizedValue.replace(/[^0-9.-]/g, "");
                                                                            // Ensure only one decimal point
                                                                            const parts = normalizedValue.split(".");
                                                                            if (parts.length > 2) {
                                                                                normalizedValue =
                                                                                    parts[0] +
                                                                                        "." +
                                                                                        parts.slice(1).join("");
                                                                            }
                                                                        }
                                                                        updateSubItemField(item.id, sectionIndex, subItem.id, "unitCost", normalizedValue);
                                                                    }, readOnly: !canEditPrices }));
                                                            })() }));
                                                    })(), (() => {
                                                        const isShopfrontOver36m = subItem.id === "shopfront-3a" ||
                                                            subItem.title
                                                                .toLowerCase()
                                                                .includes("over 3.6m");
                                                        const isAfterHours = subItem.title
                                                            .toLowerCase()
                                                            .includes("after hours");
                                                        // For shopfront-3a, display shopfront-4's unitCost in the totalCost field
                                                        if (isShopfrontOver36m) {
                                                            const shopfront4Item = section.items.find((item) => item.id === "shopfront-4");
                                                            const shopfront4Value = shopfront4Item?.unitCost || "";
                                                            // Only show input if toggle is YES
                                                            if (subItem.inScope) {
                                                                return (_jsxs("div", { className: `${styles.tableCell} ${styles.tableCellWithLabel}`, children: [_jsx("label", { children: shopfront4Item?.title }), _jsx("input", { type: "text", className: styles.costInput, value: shopfront4Value, onChange: (e) => {
                                                                                // Update shopfront-4's unitCost when user edits
                                                                                const shopfront4Index = section.items.findIndex((item) => item.id === "shopfront-4");
                                                                                if (shopfront4Index !== -1) {
                                                                                    const normalizedValue = e.target.value
                                                                                        .replace(/,/g, ".")
                                                                                        .replace(/[^0-9.-]/g, "");
                                                                                    const parts = normalizedValue.split(".");
                                                                                    const finalValue = parts.length > 2
                                                                                        ? parts[0] +
                                                                                            "." +
                                                                                            parts.slice(1).join("")
                                                                                        : normalizedValue;
                                                                                    updateSubItemField(item.id, sectionIndex, "shopfront-4", "unitCost", finalValue);
                                                                                }
                                                                            }, readOnly: !canEditPrices })] }));
                                                            }
                                                            else {
                                                                return (_jsx("div", { className: styles.tableCell }));
                                                            }
                                                        }
                                                        if (isAfterHours) {
                                                            return (_jsx("div", { className: styles.tableCell }));
                                                        }
                                                        return (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.costInput, value: subItem.totalCost || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "totalCost", e.target.value), readOnly: !canEditPrices }) }));
                                                    })(), (() => {
                                                        const isAfterHours = subItem.title
                                                            .toLowerCase()
                                                            .includes("after hours");
                                                        const isShopfrontOver36m = subItem.id === "shopfront-3a" ||
                                                            subItem.title
                                                                .toLowerCase()
                                                                .includes("over 3.6m");
                                                        // Hide notes column for shopfront-3a and After Hours items
                                                        if (isAfterHours || isShopfrontOver36m) {
                                                            return null;
                                                        }
                                                        return (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.notesInput, value: subItem.notes || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "notes", e.target.value) }) }));
                                                    })()] }, subItem.id)))] }, sectionIndex))) })) : null }))] }, item.id))) }), _jsx("div", { className: styles.tabActionButtons, children: _jsx("button", { className: styles.saveEstimateButton, onClick: handleSave, disabled: saving, children: saving ? "Saving..." : "Save Estimate" }) })] }));
    };
    if (loading) {
        return (_jsx(_Fragment, { children: _jsxs("div", { className: styles.container, children: [renderPageHeader(), _jsx("div", { className: styles.loading, children: _jsx("p", { children: "Loading estimate..." }) })] }) }));
    }
    if (error && !quote) {
        return (_jsx(_Fragment, { children: _jsxs("div", { className: styles.container, children: [renderPageHeader(), _jsxs("div", { className: styles.error, children: [_jsx("p", { children: error }), _jsx("button", { onClick: loadQuoteData, className: styles.retryBtn, children: "Retry" }), _jsx("button", { onClick: handleBack, className: styles.backBtn, children: "Back to Estimations" })] })] }) }));
    }
    return (_jsx(_Fragment, { children: _jsxs("div", { className: styles.container, children: [renderPageHeader(), _jsxs("div", { className: styles.header, children: [_jsx(PageHeaderWithBack, { title: "Edit Estimate", subtitle: quote?.quoteNumber, onBack: handleBack, breadcrumb: "Estimating/Edit Estimate" }), _jsxs("div", { className: styles.actions, children: [quote &&
                                    matchesQuoteStatus(quote.status, "approved") &&
                                    (quote.linkedJobId ? (_jsx(Button, { onClick: handleEditJob, variant: "primary", children: "Edit Job" })) : (_jsx(Button, { onClick: handleCreateJob, variant: "primary", disabled: isCreating, children: isCreating ? "Creating..." : "Create Job" }))), _jsx(Button, { onClick: handleViewQuote, variant: "secondary", children: "View Quote" })] })] }), _jsx("div", { className: styles.actionBar, children: _jsx("div", { className: styles.actionButtons, children: _jsxs("div", { className: styles.statusBlock, children: [_jsxs("div", { className: styles.statusDropdownWrapper, children: [_jsxs("select", { className: styles.statusDropdown, value: quoteStatus, onChange: (e) => handleStatusChange(e.target.value), children: [_jsx("option", { value: "draft", children: "Requested" }), _jsx("option", { value: "inprogress", children: "In Progress" }), _jsx("option", { value: "pending", children: "Pending Approval" }), _jsx("option", { value: "approved", children: "Approved" }), _jsx("option", { value: "declined", children: "Declined" }), _jsx("option", { value: "cancelled", children: "Cancelled" })] }), _jsx("span", { className: styles.dropdownIcon, children: "keyboard_arrow_down" })] }), quote &&
                                    matchesQuoteStatus(quote.status, "declined") &&
                                    quote.reasonForDecline && (_jsxs("div", { className: styles.declineReasonNote, children: ["Reason for Decline: ", quote.reasonForDecline] }))] }) }) }), error && (_jsx("div", { className: styles.errorBanner, children: _jsx("p", { children: error }) })), _jsx("div", { className: styles.tabsAndActionsRow, children: _jsx("nav", { className: styles.navTabs, children: _jsx("div", { className: styles.tabList, role: "tablist", children: ESTIMATE_TABS.map((tab) => (_jsxs("button", { type: "button", className: `${styles.tabButton} ${activeEstimateTab === tab.id ? styles.active : ""}`, onClick: () => setActiveEstimateTab(tab.id), role: "tab", "aria-selected": activeEstimateTab === tab.id, children: [tab.label, tab.id === "notifications" && unreadCount > 0 && (_jsx("span", { className: styles.tabBadge, children: unreadCount > 99 ? "99+" : unreadCount }))] }, tab.id))) }) }) }), activeEstimateTab === "detail" && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.formSection, children: _jsxs("div", { className: styles.formGrid, children: [_jsxs("div", { className: styles.leftColumn, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Site" }), _jsxs("select", { className: "form-select", value: siteId, onChange: (e) => {
                                                            const newSiteId = e.target.value;
                                                            setSiteId(newSiteId);
                                                            const selectedSite = sites.find((s) => s.id === newSiteId);
                                                            if (selectedSite && newSiteId) {
                                                                sitesService
                                                                    .getSite(newSiteId)
                                                                    .then((resp) => {
                                                                    setLinkedSiteName(resp.data.siteName || "");
                                                                    setLinkedSiteSuburb(resp.data.suburb || "");
                                                                    setShoppingCentreId(resp.data.shoppingCentreId ??
                                                                        resp.data.shoppingCentre?.id ??
                                                                        (isUuid(resp.data.shoppingCenter ?? "")
                                                                            ? resp.data.shoppingCenter
                                                                            : "") ??
                                                                        "");
                                                                })
                                                                    .catch(() => {
                                                                    setLinkedSiteName(selectedSite.name);
                                                                    setLinkedSiteSuburb("");
                                                                    setShoppingCentreId("");
                                                                });
                                                            }
                                                            else {
                                                                setLinkedSiteName("");
                                                                setLinkedSiteSuburb("");
                                                                setShoppingCentreId("");
                                                            }
                                                        }, children: [_jsx("option", { value: "", children: "Select" }), sites.map((site) => (_jsx("option", { value: site.id, children: site.name }, site.id)))] }), linkedSiteName && (_jsxs("div", { className: styles.siteDetails, children: [_jsx("span", { className: styles.siteNameDisplay, children: linkedSiteName }), linkedSiteSuburb && (_jsxs("span", { className: styles.siteSuburbDisplay, children: [", ", linkedSiteSuburb] }))] }))] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Shopping centre" }), _jsx("div", { className: styles.shoppingCentreField, children: _jsx(SearchableShoppingCentreSelect, { value: shoppingCentreId, onChange: setShoppingCentreId }) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Client Name" }), _jsxs("div", { className: styles.searchableDropdown, ref: companyDropdownRef, children: [_jsxs("div", { className: styles.searchInputWrapper, children: [_jsx("input", { type: "text", className: styles.searchInput, placeholder: "Type here to search", value: companySearchTerm, onChange: (e) => {
                                                                            setCompanySearchTerm(e.target.value);
                                                                            if (!showCompanyDropdown)
                                                                                setShowCompanyDropdown(true);
                                                                        }, onFocus: () => setShowCompanyDropdown(true) }), selectedCompanyName && (_jsx("button", { type: "button", className: styles.clearButton, onClick: handleClearCompany, title: "Clear selection", children: "\u00D7" }))] }), showCompanyDropdown && (_jsx("div", { className: styles.dropdownList, children: loadingCompanyNames ? (_jsx("div", { className: styles.dropdownLoading, children: "Loading..." })) : companyNames.length > 0 ? (companyNames.slice(0, 50).map((name) => (_jsx("div", { className: styles.dropdownItem, onClick: () => handleCompanySelect(name), children: name }, name)))) : (_jsx("div", { className: styles.dropdownEmpty, children: companySearchTerm
                                                                        ? "No companies found"
                                                                        : "Type to search..." })) }))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Attention" }), _jsxs("div", { className: styles.searchableDropdown, ref: attentionDropdownRef, children: [_jsxs("div", { className: styles.searchInputWrapper, children: [_jsx("input", { type: "text", className: styles.searchInput, placeholder: !selectedCompanyName
                                                                            ? "Select a company first"
                                                                            : "Type here to search", value: attentionSearchTerm, onChange: (e) => {
                                                                            setAttentionSearchTerm(e.target.value);
                                                                            if (!showAttentionDropdown)
                                                                                setShowAttentionDropdown(true);
                                                                        }, onFocus: () => {
                                                                            if (selectedCompanyName) {
                                                                                setShowAttentionDropdown(true);
                                                                            }
                                                                        }, disabled: !selectedCompanyName }), selectedAttentionContactName && (_jsx("button", { type: "button", className: styles.clearButton, onClick: handleClearAttentionContact, title: "Clear selection", children: "\u00D7" }))] }), showAttentionDropdown && selectedCompanyName && (_jsx("div", { className: styles.dropdownList, children: filteredAttentionContacts.length > 0 ? (filteredAttentionContacts.map((contact) => (_jsxs("div", { className: styles.dropdownItem, onClick: () => handleAttentionContactSelect(contact), children: [_jsx("div", { style: { fontWeight: "500" }, children: formatFullName(contact.firstName, contact.lastName) }), contact.email && (_jsx("div", { style: {
                                                                                fontSize: "0.85em",
                                                                                color: "#666",
                                                                            }, children: contact.email })), selectedCompanyName && (_jsx("div", { style: {
                                                                                fontSize: "0.85em",
                                                                                color: "#888",
                                                                            }, children: selectedCompanyName }))] }, contact.contactId)))) : (_jsx("div", { className: styles.dropdownEmpty, children: attentionSearchTerm
                                                                        ? "No contacts found"
                                                                        : "No contacts available" })) }))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Type" }), _jsxs("div", { className: styles.toggleWrapper, children: [_jsx("span", { className: `${styles.toggleLabel} ${defitType === "full" ? styles.active : ""}`, children: "Full Defit" }), _jsx("button", { className: styles.defitToggleButton, onClick: () => setDefitType(defitType === "full" ? "partial" : "full"), "aria-label": "Toggle defit type", children: _jsx("span", { className: `${styles.defitToggleSlider} ${defitType === "partial" ? styles.right : ""}` }) }), _jsx("span", { className: `${styles.toggleLabel} ${defitType === "partial" ? styles.active : ""}`, children: "Partial Defit" })] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Estimation Name" }), _jsx("input", { type: "text", className: "form-input", value: quoteName, onChange: (e) => setQuoteName(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroupCol} form-group-col`, children: [_jsx("label", { className: "form-label", children: "Estimation Description" }), _jsx("textarea", { className: "form-textarea", value: quoteDescription, onChange: (e) => setQuoteDescription(e.target.value), rows: 5 })] })] }), _jsxs("div", { className: styles.rightColumn, children: [_jsxs("div", { className: styles.dateTimeSection, children: [_jsxs("div", { className: styles.dateTimeRow, children: [_jsx("div", { className: styles.dateTimeBlock, children: _jsxs("div", { className: styles.dateTimeInputs, children: [_jsx("label", { className: `form-label ${styles.dateTimeLabel}`, children: "Commencement Date and Time" }), _jsx("div", { className: styles.dateInput, children: _jsx(CommencementDateInput, { className: "form-input", value: commencementDate, onChange: setCommencementDate }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: "form-input", value: commencementTime, onChange: (e) => setCommencementTime(e.target.value), placeholder: "HH:MM" }) })] }) }), _jsx("div", { className: styles.dateTimeBlock, children: _jsxs("div", { className: styles.dateTimeInputs, children: [_jsx("label", { className: `form-label ${styles.dateTimeLabel}`, children: "Completion Date and Time" }), _jsx("div", { className: styles.dateInput, children: _jsx(DateInput, { className: "form-input", value: completionDate, onChange: setCompletionDate, onInvalid: () => success("Please enter a valid date.") }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: "form-input", value: completionTime, onChange: (e) => setCompletionTime(e.target.value), placeholder: "HH:MM" }) })] }) })] }), _jsxs("div", { className: styles.durationGroup, children: [_jsx("label", { className: "form-label", children: "Project Duration (days)" }), _jsx("input", { type: "number", className: styles.durationInput, value: projectDuration, onChange: (e) => setProjectDuration(e.target.value) })] })] }), _jsxs("div", { children: [_jsx("h3", { className: styles.sectionTitle, children: "AREA INFORMATION" }), _jsx("div", { className: styles.areaInfoGrid, children: areaInfo.map((item, index) => (_jsxs("div", { className: styles.areaInfoRow, children: [_jsx("label", { className: styles.areaLabel, children: item.label }), _jsx("input", { type: "number", className: styles.areaInput, value: item.value, onFocus: (e) => {
                                                                        if (item.value === 0)
                                                                            e.target.select();
                                                                    }, onChange: (e) => handleAreaInfoChange(index, Number(e.target.value)) })] }, index))) })] })] })] }) }), _jsxs("div", { className: styles.workDetailsSection, children: [_jsx("h3", { className: styles.workDetailsTitle, children: "Work Details" }), _jsx("div", { className: styles.categoryTabsWrapper, children: _jsx("div", { className: styles.categoryTabList, children: WORK_DETAILS_CATEGORY_TABS.map((tab) => (_jsx("button", { className: `${styles.categoryTab} ${activeWorkCategory === tab.id ? styles.active : ""}`, onClick: () => {
                                                setActiveWorkCategory(tab.id);
                                                setExpandedAccordions(new Set());
                                            }, children: tab.label }, tab.id))) }) }), activeWorkCategory !== "summary" && (_jsxs("div", { className: styles.accordionControls, children: [_jsxs("button", { className: styles.controlBtn, onClick: handleExpandAll, children: [_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M7 13l5 5 5-5M7 6l5 5 5-5" }) }), "Expand All"] }), _jsxs("button", { className: styles.controlBtn, onClick: handleCollapseAll, children: [_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M17 11l-5-5-5 5M17 18l-5-5-5 5" }) }), "Collapse All"] }), _jsx("button", { className: styles.controlBtn, onClick: handleRefreshPrices, disabled: refreshingPrices, children: refreshingPrices ? "Refreshing..." : "Refresh Prices" })] })), _jsx("div", { className: styles.accordionContainer, children: renderAccordionContent() })] })] })), activeEstimateTab === "documents" && (_jsx("div", { className: styles.tabContentArea, children: _jsx(EstimatingQuoteDocumentsTab, { quoteId: quote?.id ?? estimateId }) })), activeEstimateTab === "notifications" && (_jsx("div", { className: styles.tabContentArea, children: _jsx(EstimatingNotificationsTab, { contactId: selectedContactId, quoteId: quote?.id ?? estimateId, onMarkedAsRead: clearBadge }) }))] }) }));
};
